/* Card screens: Projects, Customers, Settings (with master-detail) */
(function () {
  const { money, fmtDate, fmtDateLong } = window.AC;

  function projectFinance(db, projectId) {
    const deliverables = new Set(
      db.projects
        .find((p) => p.id === projectId)
        ?.projectDeliverables
        .map((s) => s.id) || []
    );
    const invs = db.invoices.filter((iv) => (iv.lines || []).some((line) => line.projectId === projectId || deliverables.has(line.projectDeliverableId)));
    let billed = 0, outstanding = 0;
    invs.forEach((iv) => {
      const e = MODEL.enrich(db, iv);
      if (!MODEL.isDraft(iv) && !MODEL.isCancelled(iv)) billed += e.totals.total;
      if (e.open) outstanding += e.totals.outstanding;
    });
    return { invs, billed, outstanding };
  }

  function projectIconDataUrl(project) {
    return project.projectIconContentType && project.projectIconDataBase64
      ? `data:${project.projectIconContentType};base64,${project.projectIconDataBase64}`
      : "";
  }

  function customerFinance(db, customerId) {
    const invs = db.invoices.filter((iv) => iv.customerId === customerId);
    let billed = 0, outstanding = 0;
    invs.forEach((iv) => {
      const e = MODEL.enrich(db, iv);
      if (!MODEL.isDraft(iv) && !MODEL.isCancelled(iv)) billed += e.totals.total;
      if (e.open) outstanding += e.totals.outstanding;
    });
    return { invs, billed, outstanding };
  }

  function ProjectTicketEditor({ project, plan, editing, onChange, compact }) {
    const statusOptions = window.AC.PROJECT_TICKET_STATUSES;
    const categoryOptions = window.AC.projectTicketCategoryOptions(project);
    const clusterDefaults = window.AC.PROJECT_TICKET_CLUSTER_NAMES;
    const categories = (plan && plan.categories) || [];
    const categoryCount = categories.length;
    const totalItems = categories.reduce((sum, category) => sum + category.items.length, 0);
    const [remarksTarget, setRemarksTarget] = React.useState(null);
    const remarksSelection = remarksTarget && categories.reduce((found, category) => {
      if (found || category.id !== remarksTarget.categoryId) return found;
      const item = category.items.find((ticket) => ticket.id === remarksTarget.ticketId);
      return item ? { category, item } : null;
    }, null);

    function patchPlan(nextCategories) {
      onChange({ ...plan, categories: nextCategories });
    }

    function updateCategory(categoryId, patch) {
      patchPlan(categories.map((category) => category.id === categoryId ? {
        ...category,
        ...patch,
      } : category));
    }

    function addCategory() {
      const used = new Set(categories.map((category) => category.name));
      const name = clusterDefaults.find((category) => !used.has(category)) || "General";
      patchPlan([...categories, { id: `category-${Date.now()}-${Math.random().toString(16).slice(2)}`, name, items: [] }]);
    }

    function removeCategory(categoryId) {
      patchPlan(categories.filter((category) => category.id !== categoryId));
    }

    function updateItem(categoryId, itemId, patch) {
      patchPlan(categories.map((category) => {
        if (category.id !== categoryId) return category;
        return {
          ...category,
          items: category.items.map((item) => {
            if (item.id !== itemId) return item;
            const next = { ...item, ...patch };
            if (Object.prototype.hasOwnProperty.call(patch, "done")) next.status = patch.done ? "Done" : (item.status === "Done" ? "Open" : item.status);
            if (patch.status) next.done = patch.status === "Done";
            return next;
          }),
        };
      }));
    }

    function addItem(categoryId) {
      patchPlan(categories.map((category) => category.id === categoryId ? {
        ...category,
        items: [...category.items, window.AC.newProjectTicket()],
      } : category));
    }

    function removeItem(categoryId, itemId) {
      patchPlan(categories.map((category) => category.id === categoryId ? {
        ...category,
        items: category.items.filter((item) => item.id !== itemId),
      } : category));
    }

    return (
      <div className={compact ? "ticket-clusters compact" : "ticket-clusters"}>
        <div className="ticket-toolbar">
          <span className="muted" style={{ fontSize: 12 }}>{categoryCount} cluster{categoryCount !== 1 ? "s" : ""} · {totalItems} ticket{totalItems !== 1 ? "s" : ""}</span>
          {editing && <button className="btn sm ghost" onClick={addCategory}><Icon name="plus" size={14} />Category</button>}
        </div>
        {categories.map((category) => {
          const doneCount = category.items.filter((item) => item.done).length;
          return (
            <div className="ticket-cluster" key={category.id}>
              <div className="ticket-cluster-head">
                {editing
                  ? <input className="inp ticket-category-input" value={category.name} onChange={(event) => updateCategory(category.id, { name: event.target.value })} />
                  : <strong>{category.name}</strong>}
                <span className="chip">{doneCount}/{category.items.length} done</span>
                {editing && <button className="icon-btn" title="Add ticket" onClick={() => addItem(category.id)}><Icon name="plus" size={14} /></button>}
                {editing && categories.length > 1 && <button className="icon-btn" title="Remove category" onClick={() => removeCategory(category.id)}><Icon name="trash" size={14} /></button>}
              </div>
              <div className="ticket-items">
                {category.items.map((item) => (
                  <div className="ticket-item" key={item.id}>
                    <div className="ticket-line">
                      <input type="checkbox" title="Mark ticket done" disabled={!editing} checked={!!item.done} onChange={(event) => updateItem(category.id, item.id, { done: event.target.checked })} />
                      {editing
                        ? <input className="inp" value={item.title} onChange={(event) => updateItem(category.id, item.id, { title: event.target.value })} placeholder="Ticket" />
                        : <span className="ticket-title" style={{ textDecoration: item.done ? "line-through" : "none" }}>{item.title || "Untitled ticket"}</span>}
                      {editing
                        ? <select className="inp ticket-status" value={item.status} onChange={(event) => updateItem(category.id, item.id, { status: event.target.value })}>{statusOptions.map((status) => <option key={status}>{status}</option>)}</select>
                        : <Pill kind={item.done ? "Completed" : item.status} sm />}
                      {editing && <span className="ticket-actions">
                        <button className="icon-btn" title={item.remarks ? "Edit remarks" : "Add remarks"} onClick={() => setRemarksTarget({ categoryId: category.id, ticketId: item.id })}><Icon name="doc" size={14} /></button>
                        <button className="icon-btn" title="Remove ticket" onClick={() => removeItem(category.id, item.id)}><Icon name="trash" size={14} /></button>
                      </span>}
                    </div>
                    {editing ? (
                      <div className="ticket-meta-grid">
                        <button className={'ticket-remarks-trigger' + (item.remarks ? '' : ' empty')} onClick={() => setRemarksTarget({ categoryId: category.id, ticketId: item.id })}>
                          <Icon name="doc" size={13} />
                          <span>{item.remarks || 'Add remarks'}</span>
                        </button>
                        <select className="inp" value={window.AC.cleanProjectTicketCategory(item.category)} onChange={(event) => updateItem(category.id, item.id, { category: event.target.value })}>
                          {categoryOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        <input className="inp" value={item.after} onChange={(event) => updateItem(category.id, item.id, { after: event.target.value })} placeholder="Continue after item X" />
                      </div>
                    ) : (item.remarks || item.after) && (
                      <div className="ticket-meta">
                        {item.remarks && <span>{item.remarks}</span>}
                        {item.after && <span className="chip"><Icon name="link" size={11} />After {item.after}</span>}
                      </div>
                    )}
                  </div>
                ))}
                {category.items.length === 0 && <span className="muted" style={{ fontSize: 12 }}>No tickets in this cluster.</span>}
              </div>
            </div>
          );
        })}
        <TicketRemarksDrawer
          open={!!remarksSelection}
          item={remarksSelection && remarksSelection.item}
          categoryName={remarksSelection && remarksSelection.category.name}
          onClose={() => setRemarksTarget(null)}
          onSave={(remarks) => {
            if (!remarksSelection) return;
            updateItem(remarksSelection.category.id, remarksSelection.item.id, { remarks });
            setRemarksTarget(null);
          }}
        />
      </div>
    );
  }

  window.ProjectTicketEditor = ProjectTicketEditor;

  function StatTriple({ items }) {
    return (
      <div className="rc-stats">
        {items.map((it, i) => (
          <div className="rc-stat" key={i}>
            <div className="k">{it.k}</div>
            <div className="v" style={it.tone ? { color: `var(--${it.tone})` } : null}>{it.v}</div>
          </div>
        ))}
      </div>
    );
  }

  function projectIdFromHash() {
    const match = window.location.hash.match(/^#\/?projects\/([^/]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  function activeActionCenterRunsForProject(actionCenterOverview, projectId) {
    return ((actionCenterOverview && actionCenterOverview.runs) || []).filter((run) => {
      const status = String(run.status || '').trim().toLowerCase();
      if (status === 'done' || status === 'error') return false;
      return String(run.projectId || '') === String(projectId || '')
        || (String(run.contextScopeType || '').toLowerCase() === 'project' && String(run.contextScopeId || '') === String(projectId || ''));
    });
  }

  function Projects({ db, query, currentEmployeeId, actionCenterOverview, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, bridgeActivityByRunId, onDrawer, onAction, onNav, onToast, onError, onReload, onVoiceContextChange }) {
    const [sel, setSel] = React.useState(projectIdFromHash);
    const [docDropProjectId, setDocDropProjectId] = React.useState("");
    const project = db.projects.find((p) => p.id === sel);
    const selectProject = (projectId) => {
      setSel(projectId);
      window.location.hash = projectId ? `projects/${encodeURIComponent(projectId)}` : "projects";
    };

    React.useEffect(() => {
      const syncProject = () => setSel(projectIdFromHash());
      window.addEventListener("hashchange", syncProject);
      return () => window.removeEventListener("hashchange", syncProject);
    }, []);

    if (project) return <ProjectDetailView db={db} project={project} currentEmployeeId={currentEmployeeId} actionCenterOverview={actionCenterOverview} bridgeActiveEmployeeIds={bridgeActiveEmployeeIds} bridgeActivityByEmployeeId={bridgeActivityByEmployeeId} bridgeActivityByRunId={bridgeActivityByRunId} onBack={() => selectProject(null)} onDrawer={onDrawer} onAction={onAction} onToast={onToast} onError={onError} onReload={onReload} onVoiceContextChange={onVoiceContextChange} />;

    let list = db.projects.slice();
    if (query) { const q = query.toLowerCase(); list = list.filter((p) => p.name.toLowerCase().includes(q) || (p.kind || '').toLowerCase().includes(q)); }

    return (
      <AiEmployeeContextActions db={db} onToast={onToast} onError={onError}>
        {({ contextMenuProps }) => (
          <div>
            <Toolbar>
              <span className="muted" style={{ fontSize: 12 }}>{db.projects.filter((p) => p.status === 'Active').length} active · {db.projects.length} total</span>
              <div style={{ marginLeft: 'auto' }}><button className="btn" onClick={() => onDrawer('project')}><Icon name="plus" size={14} />New project</button></div>
            </Toolbar>
            <div className="project-list">
              {list.map((p) => {
                const fin = projectFinance(db, p.id);
                const pr = window.ProjectProgress(p);
                const pct = Math.round(pr.msPct * 100);
                const custNames = p.customerIds.map((id) => { const c = db.customers.find((x) => x.id === id); return c ? c.name : null; }).filter(Boolean);
                const phaseSummary = MODEL.projectPhaseSummary(p);
                const currentPhaseWorkPackage = phaseSummary.current;
                const phasePillKind = currentPhaseWorkPackage && currentPhaseWorkPackage.phase === 'Done' ? 'Active'
                  : currentPhaseWorkPackage && currentPhaseWorkPackage.phase === 'Gathering' ? 'Draft'
                    : 'Open';
                const phasePillLabel = currentPhaseWorkPackage
                  ? (phaseSummary.total > 1 ? `${currentPhaseWorkPackage.name}: ${currentPhaseWorkPackage.phase}` : currentPhaseWorkPackage.phase)
                  : null;
                const iconDataUrl = projectIconDataUrl(p);
                const dropProps = window.projectDocumentDropProps
                  ? window.projectDocumentDropProps(p, onAction, {}, (over) => setDocDropProjectId(over ? p.id : ""))
                  : {};
                const scopedRuns = activeActionCenterRunsForProject(actionCenterOverview, p.id);
                return (
                  <button type="button" className={"project-list-item" + (docDropProjectId === p.id ? " doc-over" : "")} key={p.id} {...dropProps} {...contextMenuProps(window.projectAiContextReference(p, custNames, pr, fin))} onClick={() => selectProject(p.id)}>
                <div className="project-list-summary">
                  <div className="project-list-icon" aria-hidden="true">
                    {iconDataUrl ? <img src={iconDataUrl} alt="" /> : <Icon name="projects" size={21} />}
                  </div>
                <div className="project-list-main">
                  <div className="project-list-head">
                    <div className="rc-id">
                      <div className="rc-title">{p.name}</div>
                      <div className="rc-meta">{p.kind} · {p.projectDeliverables.length} deliverable{p.projectDeliverables.length !== 1 ? 's' : ''}</div>
                    </div>
                    <div className="project-list-pills">
                      <ActionCenterScopeActivity runs={scopedRuns} agents={(actionCenterOverview && actionCenterOverview.agents) || []} bridgeActiveEmployeeIds={bridgeActiveEmployeeIds} bridgeActivityByEmployeeId={bridgeActivityByEmployeeId} bridgeActivityByRunId={bridgeActivityByRunId} />
                      {phasePillLabel && <Pill kind={phasePillKind} sm>{phasePillLabel}</Pill>}
                      <Pill kind={p.status} sm />
                    </div>
                  </div>
                  <div className="rc-meta project-list-customers">
                    <Icon name="customers" size={13} className="faint" />
                    <span>{custNames.length ? custNames.join(', ') : 'No customers linked'}</span>
                  </div>
                </div>
                </div>
                <div className="project-list-progress">
                  <div className="project-progress-top">
                    <span className="project-progress-pct">{pct}%</span>
                    <span className="project-progress-label">progress</span>
                  </div>
                  <span className="progressbar"><span style={{ width: pct + '%' }} /></span>
                  <div className="project-progress-meta">
                    <span>{pr.msDone}/{pr.msTotal} milestones</span>
                    <span>{pr.ticketDone}/{pr.ticketTotal} tickets</span>
                  </div>
                </div>
                <StatTriple items={[
                  { k: 'Billed', v: money(fin.billed) },
                  { k: 'Outstanding', v: fin.outstanding > 0 ? money(fin.outstanding) : '—', tone: fin.outstanding > 0 ? 'warn' : '' },
                  { k: 'Invoices', v: fin.invs.length },
                ]} />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </AiEmployeeContextActions>
    );
  }

  function Customers({ db, query, onDrawer, onAction, onToast, onError, onReload }) {
    const [sel, setSel] = React.useState(null);
    const customer = db.customers.find((c) => c.id === sel);
    if (customer) return <CustomerDetail db={db} customer={customer} onBack={() => setSel(null)} onDrawer={onDrawer} onAction={onAction} onToast={onToast} onError={onError} onReload={onReload} />;

    let list = db.customers.slice();
    if (query) { const q = query.toLowerCase(); list = list.filter((c) => c.name.toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q)); }

    return (
      <div>
        <Toolbar>
          <span className="muted" style={{ fontSize: 12 }}>{db.customers.length} customers</span>
          <div style={{ marginLeft: 'auto' }}><button className="btn" onClick={() => onDrawer('customer')}><Icon name="plus" size={14} />Add customer</button></div>
        </Toolbar>
        <div className="customer-list">
          {list.map((c) => {
            const fin = customerFinance(db, c.id);
            const projs = db.projects.filter((p) => p.customerIds.includes(c.id));
            const contactCount = (c.contactPersons || []).length;
            const noteCount = (db.notes || []).filter((item) => item.customerId === c.id).length;
            return (
              <button type="button" className="customer-list-item" key={c.id} onClick={() => setSel(c.id)}>
                <div className="customer-list-main">
                  <Avatar name={c.name} />
                  <div className="rc-id">
                    <div className="rc-title">{c.name}</div>
                    <div className="rc-meta">{c.email || c.phone || 'No contact details'}</div>
                  </div>
                  {fin.outstanding > 0 && <Pill kind="Overdue" sm>Owes</Pill>}
                </div>
                <div className="customer-list-meta">
                  <span>{projs.length} project{projs.length !== 1 ? 's' : ''}</span>
                  <span>{contactCount} contact{contactCount !== 1 ? 's' : ''}</span>
                  <span>{noteCount} note{noteCount !== 1 ? 's' : ''}</span>
                </div>
                <StatTriple items={[
                  { k: 'Billed', v: money(fin.billed) },
                  { k: 'Outstanding', v: fin.outstanding > 0 ? money(fin.outstanding) : '—', tone: fin.outstanding > 0 ? 'warn' : '' },
                  { k: 'Invoices', v: fin.invs.length },
                ]} />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function CustomerDetail({ db, customer, onBack, onDrawer, onAction, onToast, onError, onReload }) {
    const fin = customerFinance(db, customer.id);
    const projs = db.projects.filter((p) => p.customerIds.includes(customer.id));
    const contactPersons = customer.contactPersons || [];
    const customerNotes = (db.notes || []).filter((item) => item.customerId === customer.id);
    const EmailComponent = window.EmailExplorer;
    return (
      <div>
        <Toolbar>
          <button className="btn subtle sm" onClick={onBack}><Icon name="chevL" size={14} />Customers</button>
          <span className="faint">/</span><span className="t-strong">{customer.name}</span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
            <button className="btn ghost sm" onClick={() => onDrawer('customerContact', null, { customerId: customer.id })}><Icon name="plus" size={14} />Contact</button>
            <button className="btn ghost sm" onClick={() => onDrawer('note', null, { customerId: customer.id })}><Icon name="plus" size={14} />Note</button>
            <button className="btn ghost sm" onClick={() => onDrawer('customer', customer.id)}><Icon name="edit" size={14} />Edit</button>
          </div>
        </Toolbar>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1.4fr', alignItems: 'start' }}>
          <div className="grid">
            <Panel title="Profile">
              <dl className="detail-grid">
                <dt>Email</dt><dd>{customer.email || '—'}</dd>
                <dt>Phone</dt><dd>{customer.phone || '—'}</dd>
                <dt>Billing address</dt><dd style={{ whiteSpace: 'pre-line' }}>{customer.billingAddress || '—'}</dd>
                <dt>Notes</dt><dd>{customer.notes || '—'}</dd>
              </dl>
              <div className="divider" />
              <StatTriple items={[
                { k: 'Billed', v: money(fin.billed) },
                { k: 'Outstanding', v: money(fin.outstanding), tone: fin.outstanding > 0 ? 'warn' : '' },
                { k: 'Projects', v: projs.length },
              ]} />
            </Panel>
            <Panel title="Contact persons" sub={`${contactPersons.length} total`} flush>
              <DataTable
                rows={contactPersons}
                rowKey={(contact) => contact.id}
                empty="No contact persons."
                columns={[
                  { header: 'Name', render: (contact) => <div className="cellstack"><span className="t-strong">{[contact.name, contact.lastName].filter(Boolean).join(' ') || '—'}</span><span className="t-sub">{contact.remarks || '—'}</span></div> },
                  { header: 'Email', className: 'muted', render: (contact) => contact.email || '—' },
                  { header: 'Phone', className: 'muted', render: (contact) => contact.phone || '—' },
                  { header: '', className: 'num', render: (contact) => {
                    const contactName = [contact.name, contact.lastName].filter(Boolean).join(' ') || 'this contact';
                    return <span style={{ display: 'inline-flex', gap: 4 }}>
                      <button className="icon-btn" title="Edit contact" onClick={() => onDrawer('customerContact', contact.id, { customerId: customer.id })}><Icon name="edit" size={14} /></button>
                      <button className="icon-btn" title="Remove contact" onClick={() => onAction('deleteCustomerContact', { customerId: customer.id, contactId: contact.id, contactName })}><Icon name="trash" size={14} /></button>
                    </span>;
                  } },
                ]}
              />
            </Panel>
            <Panel title="Notes" sub={`${customerNotes.length} total`} flush>
              <DataTable
                rows={customerNotes}
                rowKey={(note) => note.id}
                empty="No notes."
                columns={[
                  { header: 'Note', render: (note) => <div className="cellstack"><span className="t-strong">{note.subject || 'Note'}</span><span className="t-sub">{note.body || '—'}</span></div> },
                  { header: 'Type', className: 'muted', render: (note) => note.type || 'note' },
                  { header: 'Date', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (note) => fmtDate(note.occurredOn || note.date) },
                  { header: '', className: 'num', render: (note) => (
                    <span style={{ display: 'inline-flex', gap: 4 }}>
                      <button className="icon-btn" title="Edit note" onClick={() => onDrawer('note', note.id, { customerId: customer.id })}><Icon name="edit" size={14} /></button>
                      <button className="icon-btn" title="Delete note" onClick={() => onAction('deleteNote', { id: note.id, subject: note.subject })}><Icon name="trash" size={14} /></button>
                    </span>
                  ) },
                ]}
              />
            </Panel>
            {EmailComponent && <EmailComponent db={db} query="" onToast={onToast} onError={onError} onReload={onReload} onDrawer={onDrawer} onAction={onAction} scopeCustomer={customer} showProjectDrop={false} />}
          </div>
          <Panel title="Invoices" sub={`${fin.invs.length} total`} flush>
            <DataTable
              rows={fin.invs.sort((a, b) => b.issueDate.localeCompare(a.issueDate))}
              rowKey={(iv) => iv.id}
              empty="No invoices."
              columns={[
                { header: 'Invoice', className: 'mono t-strong', render: (iv) => iv.number },
                { header: 'Issue', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (iv) => fmtDate(iv.issueDate) },
                { header: 'Due', className: 'mono muted', cellStyle: { fontSize: 11.5 }, render: (iv) => fmtDate(iv.dueDate) },
                { header: 'Status', render: (iv) => <Pill kind={MODEL.enrich(db, iv).display} sm /> },
                { header: 'Total', className: 'num', render: (iv) => money(MODEL.enrich(db, iv).totals.total) },
                { header: 'Outstanding', className: 'num', render: (iv) => { const e = MODEL.enrich(db, iv); return e.totals.outstanding > 0 ? money(e.totals.outstanding) : '—'; } },
              ]}
            />
          </Panel>
        </div>
      </div>
    );
  }

  function UsersSettings({ employees, onToast, onError }) {
    const empty = { name: "", email: "", password: "", role: "User", active: true, employeeId: "" };
    const [users, setUsers] = React.useState([]);
    const [form, setForm] = React.useState(empty);
    const [editId, setEditId] = React.useState(null);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const load = React.useCallback(async () => {
      try { setUsers(await window.API.listUsers()); }
      catch (e) { onError(e); }
      finally { setLoading(false); }
    }, [onError]);
    React.useEffect(() => { load(); }, [load]);
    const employeeById = React.useMemo(() => Object.fromEntries((employees || []).map((employee) => [employee.id, employee])), [employees]);
    const closeDrawer = () => { setDrawerOpen(false); setEditId(null); setForm(empty); };
    const create = () => {
      setEditId(null);
      setForm(empty);
      setDrawerOpen(true);
    };
    const edit = (user) => {
      setEditId(user.id);
      setForm({ name: user.name, email: user.email, password: "", role: user.role, active: !!user.active, employeeId: user.employeeId || "" });
      setDrawerOpen(true);
    };
    const save = async () => {
      try {
        editId ? await window.API.updateUser(editId, form) : await window.API.createUser(form);
        onToast(editId ? "User updated" : "User created");
        closeDrawer();
        await load();
      } catch (e) { onError(e); }
    };
    const remove = async (user) => {
      if (!window.confirm(`Delete ${user.email}?`)) return;
      try {
        await window.API.deleteUser(user.id);
        onToast("User deleted");
        if (editId === user.id) closeDrawer();
        await load();
      } catch (e) { onError(e); }
    };

    return (
      <React.Fragment>
        <Toolbar>
          <span className="muted" style={{ fontSize: 12 }}>{users.length} user{users.length === 1 ? "" : "s"}</span>
          <div style={{ marginLeft: "auto" }}>
            <button className="btn" onClick={create}><Icon name="plus" size={14} />New user</button>
          </div>
        </Toolbar>
        {loading ? <div className="muted">Loading users...</div> :
          <DataTable
            rows={users}
            rowKey={(user) => user.id}
            empty="No users."
            columns={[
              { header: "User", render: (user) => <span className="cellstack"><span className="t-strong">{user.name}</span><span className="t-sub">{user.email}</span></span> },
              { header: "Employee", render: (user) => user.employeeId && employeeById[user.employeeId] ? employeeById[user.employeeId].name : <span className="faint">None</span> },
              { header: "Role", render: (user) => <Pill kind={user.role === "Admin" ? "Open" : "Draft"}>{user.role}</Pill> },
              { header: "Status", render: (user) => <Pill kind={user.active ? "Active" : "Inactive"} sm /> },
            ]}
            actions={[
              { icon: "edit", title: "Edit", onClick: edit },
              { icon: "trash", title: "Delete", onClick: remove },
            ]}
          />}
        {drawerOpen && <React.Fragment>
          <div className="overlay" onClick={closeDrawer} />
          <aside className="drawer">
            <header>
              <div style={{ flex: 1 }}>
                <h2>{editId ? "Edit user" : "Create user"}</h2>
                <div className="sub">Application access</div>
              </div>
              <button className="icon-btn" onClick={closeDrawer}><Icon name="close" size={17} /></button>
            </header>
            <div className="body">
              <div className="field-row">
                <div className="field"><label>Name</label><input className="inp" value={form.name} onChange={(e) => set('name', e.target.value)} /></div>
                <div className="field"><label>Email</label><input className="inp" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></div>
              </div>
              <div className="field-row">
                <div className="field"><label>Password</label><input className="inp" type="password" value={form.password} onChange={(e) => set('password', e.target.value)} placeholder={editId ? "Leave blank to keep" : ""} /></div>
                <div className="field"><label>Role</label><select className="inp" value={form.role} onChange={(e) => set('role', e.target.value)}><option>Admin</option><option>User</option></select></div>
              </div>
              <div className="field"><label>Employee</label><select className="inp" value={form.employeeId} onChange={(e) => set('employeeId', e.target.value)}>
                <option value="">No employee</option>
                {(employees || []).filter((employee) => (employee.type || "Human") === "Human").map((employee) => <option key={employee.id} value={employee.id}>{employee.name}</option>)}
              </select></div>
              <label className="checkrow"><input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} /> Active</label>
            </div>
            <footer>
              <button className="btn ghost" onClick={closeDrawer}>Cancel</button>
              <button className="btn" onClick={save}><Icon name="check" size={14} />{editId ? "Save changes" : "Create"}</button>
            </footer>
          </aside>
        </React.Fragment>}
      </React.Fragment>
    );
  }

  function OrganizationSettings({ onToast, onError }) {
    const empty = { slug: "", name: "", adminName: "", adminEmail: "", adminPassword: "" };
    const [organizations, setOrganizations] = React.useState([]);
    const [form, setForm] = React.useState(empty);
    const [drawerOpen, setDrawerOpen] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [savingSlug, setSavingSlug] = React.useState("");
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const load = React.useCallback(async () => {
      try { setOrganizations(await window.API.listOrganizations()); }
      catch (e) { onError(e); }
      finally { setLoading(false); }
    }, [onError]);
    React.useEffect(() => { load(); }, [load]);
    const closeDrawer = () => { setDrawerOpen(false); setForm(empty); };
    const create = async () => {
      try {
        await window.API.createOrganization({ ...form, slug: (form.slug || "").trim().toLowerCase() });
        onToast("Organization created");
        closeDrawer();
        await load();
      } catch (e) { onError(e); }
    };
    const update = async (organization, patch) => {
      try {
        setSavingSlug(organization.slug);
        await window.API.updateOrganization(organization.slug, { name: organization.name, active: organization.active, ...patch });
        onToast("Organization updated");
        await load();
      } catch (e) { onError(e); }
      finally { setSavingSlug(""); }
    };

    return (
      <React.Fragment>
        <Toolbar>
          <span className="muted" style={{ fontSize: 12 }}>{organizations.length} organization{organizations.length === 1 ? "" : "s"}</span>
          <div style={{ marginLeft: "auto" }}>
            <button className="btn" onClick={() => setDrawerOpen(true)}><Icon name="plus" size={14} />New organization</button>
          </div>
        </Toolbar>
        {loading ? <div className="muted">Loading organizations...</div> :
          <DataTable
            rows={organizations}
            rowKey={(organization) => organization.slug}
            empty="No organizations."
            columns={[
              { header: "Organization", render: (organization) => <span className="cellstack"><span className="t-strong">{organization.name}</span><span className="t-sub mono">{organization.slug}</span></span> },
              { header: "Status", render: (organization) => <Pill kind={organization.active ? "Active" : "Inactive"} sm /> },
              { header: "Created", className: "mono muted", render: (organization) => fmtDate(organization.createdAt) },
            ]}
            actions={[
              { icon: "edit", title: "Rename", onClick: (organization) => {
                const name = window.prompt("Organization name", organization.name);
                if (name && name.trim() && name.trim() !== organization.name) update(organization, { name: name.trim() });
              } },
              { icon: "check", title: "Activate", onClick: (organization) => update(organization, { active: true }) },
              { icon: "close", title: "Deactivate", onClick: (organization) => {
                if (window.confirm(`Deactivate ${organization.name}? Users will no longer be able to sign in to this organization.`)) update(organization, { active: false });
              } },
            ]}
          />}
        {savingSlug && <div className="muted" style={{ marginTop: 8, fontSize: 12 }}>Saving {savingSlug}...</div>}
        {drawerOpen && <React.Fragment>
          <div className="overlay" onClick={closeDrawer} />
          <aside className="drawer">
            <header>
              <div style={{ flex: 1 }}>
                <h2>Create organization</h2>
                <div className="sub">Independent company workspace</div>
              </div>
              <button className="icon-btn" onClick={closeDrawer}><Icon name="close" size={17} /></button>
            </header>
            <div className="body">
              <div className="field-row">
                <div className="field"><label>Slug</label><input className="inp" value={form.slug} onChange={(e) => set('slug', e.target.value.toLowerCase())} placeholder="acme" pattern="(?:[a-z0-9]|-){3,40}" /></div>
                <div className="field"><label>Name</label><input className="inp" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Acme GmbH" /></div>
              </div>
              <div className="divider" />
              <div className="field"><label>First admin name</label><input className="inp" value={form.adminName} onChange={(e) => set('adminName', e.target.value)} /></div>
              <div className="field"><label>First admin email</label><input className="inp" type="email" value={form.adminEmail} onChange={(e) => set('adminEmail', e.target.value)} /></div>
              <div className="field"><label>First admin password</label><input className="inp" type="password" value={form.adminPassword} onChange={(e) => set('adminPassword', e.target.value)} minLength={8} /></div>
            </div>
            <footer>
              <button className="btn ghost" onClick={closeDrawer}>Cancel</button>
              <button className="btn" onClick={create}><Icon name="check" size={14} />Create</button>
            </footer>
          </aside>
        </React.Fragment>}
      </React.Fragment>
    );
  }

  function StorageSettings({ db, form, set, onToast, onError, onReload }) {
    const providers = db.storageProviders || [];
    const connections = db.storageConnections || [];
    const [provider, setProvider] = React.useState(providers[0] ? providers[0].provider : 'dropbox');
    const [busy, setBusy] = React.useState("");
    React.useEffect(() => {
      if (providers.length && !providers.some((item) => item.provider === provider)) setProvider(providers[0].provider);
    }, [providers, provider]);
    const providerMeta = (id) => providers.find((item) => item.provider === id) || { provider: id, label: id, configured: false, scopes: '' };
    const storageCredentialFields = [
      { provider: 'dropbox', label: 'Dropbox', clientId: 'dropboxStorageClientId', clientSecret: 'dropboxStorageClientSecret' },
      { provider: 'google-drive', label: 'Google Drive', clientId: 'googleDriveStorageClientId', clientSecret: 'googleDriveStorageClientSecret' },
      { provider: 'onedrive', label: 'OneDrive', clientId: 'oneDriveStorageClientId', clientSecret: 'oneDriveStorageClientSecret' },
    ];
    const origin = window.location.origin;
    const addConnection = async () => {
      const meta = providerMeta(provider);
      try {
        setBusy(`add:${meta.provider}`);
        await window.API.createStorageConnection(meta.provider);
        if (onToast) onToast('Storage connection row added');
        if (onReload) await onReload();
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setBusy("");
      }
    };
    const authorize = async (row) => {
      const authTab = window.open('', '_blank', 'noreferrer');
      try {
        setBusy(row.id || row.provider);
        const result = await window.API.startStorageConnection(row.provider, row.id);
        if (authTab) authTab.location.href = result.authorizationUrl;
        else window.location.href = result.authorizationUrl;
        if (onToast) onToast('Authorization opened in a new tab');
      } catch (e) {
        if (authTab) authTab.close();
        if (onError) onError(e);
      } finally {
        setBusy("");
      }
    };
    const createBaseFolder = async (row) => {
      const name = window.prompt('Base folder name', row.baseFolderName || 'Appligator');
      if (name === null) return;
      try {
        setBusy(`base:${row.id}`);
        await window.API.configureStorageBaseFolder(row.id, { create: true, name });
        if (onToast) onToast('Base folder created');
        if (onReload) await onReload();
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setBusy("");
      }
    };
    const setExistingBaseFolder = async (row) => {
      const name = window.prompt('Base folder name', row.baseFolderName || 'Appligator');
      if (name === null) return;
      const folderId = window.prompt(row.provider === 'dropbox' ? 'Dropbox folder path' : 'Folder ID', row.baseFolderId || row.baseFolderPath || '');
      if (folderId === null) return;
      const folderUrl = window.prompt('Folder URL (optional)', row.baseFolderUrl || '');
      if (folderUrl === null) return;
      try {
        setBusy(`base:${row.id}`);
        await window.API.configureStorageBaseFolder(row.id, {
          create: false,
          name,
          folderId: row.provider === 'dropbox' ? '' : folderId,
          folderPath: row.provider === 'dropbox' ? folderId : '',
          folderUrl
        });
        if (onToast) onToast('Base folder saved');
        if (onReload) await onReload();
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setBusy("");
      }
    };
    const disconnect = async (row) => {
      if (!window.confirm(`Remove ${row.accountName || row.label || row.provider}?`)) return;
      try {
        setBusy(row.id);
        await window.API.disconnectStorageConnection(row.id);
        if (onToast) onToast('Storage connection removed');
        if (onReload) await onReload();
      } catch (e) {
        if (onError) onError(e);
      } finally {
        setBusy("");
      }
    };
    const rows = [
      ...connections.map((item) => {
        const meta = providerMeta(item.provider);
        return { ...item, label: meta.label || item.provider, configured: meta.configured, scopes: item.scopes || meta.scopes, status: item.authorized ? 'Connected' : 'Not authorized' };
      }),
    ];

    return (
      <div role="tabpanel" aria-label="Storage accounts">
        <div className="settings-block-head">
          <h3>Provider OAuth apps</h3>
        </div>
        <div className="settings-list">
          {storageCredentialFields.map((item) => (
            <div className="settings-item" key={item.provider}>
              <div>
                <strong>{item.label}</strong>
                <span className="muted mono">{origin}/api/storage/{item.provider}/callback</span>
              </div>
              <div className="field-row" style={{ marginTop: 10 }}>
                <div className="field"><label>Client ID</label><input className="inp" value={form[item.clientId] || ''} onChange={(e) => set(item.clientId, e.target.value)} placeholder={`${item.label} client ID`} /></div>
                <div className="field"><label>Client secret</label><input className="inp" type="password" value={form[item.clientSecret] || ''} onChange={(e) => set(item.clientSecret, e.target.value)} placeholder="Leave unchanged" /></div>
              </div>
            </div>
          ))}
        </div>
        <div className="divider" />
        <div className="settings-block-head">
          <h3>Connections</h3>
        </div>
        <Toolbar>
          <div className="field" style={{ margin: 0, minWidth: 260 }}>
            <label>Provider</label>
            <select className="inp" value={provider} onChange={(e) => setProvider(e.target.value)}>
              {providers.map((item) => <option key={item.provider} value={item.provider}>{item.label}</option>)}
            </select>
          </div>
          <button className="btn" onClick={addConnection} disabled={!provider || busy === `add:${provider}`}><Icon name="plus" size={14} />Add connection</button>
        </Toolbar>
        <DataTable
          rows={rows}
          rowKey={(row) => row.id || row.key}
          empty="No storage connections."
          columns={[
            { header: 'Provider', render: (row) => <span className="cellstack"><span className="t-strong">{row.label || row.provider}</span><span className="t-sub">{row.provider}</span></span> },
            { header: 'Account', render: (row) => row.accountName || <span className="muted">Pending authorization</span> },
            { header: 'Status', render: (row) => <Pill kind={row.status === 'Connected' ? 'Active' : 'Draft'} sm>{row.configured === false ? 'Not configured' : row.status}</Pill> },
            { header: 'Base folder', render: (row) => row.baseFolderName ? <span className="cellstack"><span className="t-strong">{row.baseFolderName}</span><span className="t-sub">{row.baseFolderPath || row.baseFolderId}</span></span> : <span className="muted">Not set</span> },
            { header: 'Scopes', render: (row) => <span className="t-sub">{row.scopes || '-'}</span> },
          ]}
          actions={[
            { label: 'Authorize', icon: 'external', title: 'Authorize in provider', show: (row) => !row.authorized || row.configured !== false, disabled: (row) => row.configured === false || busy === (row.id || row.provider), onClick: authorize, className: 'btn sm ghost' },
            { label: 'Create base', icon: 'plus', title: 'Create base folder', show: (row) => row.authorized, disabled: (row) => busy === `base:${row.id}`, onClick: createBaseFolder, className: 'btn sm ghost' },
            { label: 'Set base', icon: 'link', title: 'Set existing base folder', show: (row) => row.authorized, disabled: (row) => busy === `base:${row.id}`, onClick: setExistingBaseFolder, className: 'btn sm ghost' },
            { icon: 'trash', title: 'Remove', onClick: disconnect },
          ]}
        />
      </div>
    );
  }

  function NotificationSettings({ onToast, onError }) {
    const [push, setPush] = React.useState({ supported: false, enabled: false, subscribed: false, busy: true, status: 'Checking notifications...' });

    const refresh = React.useCallback(async () => {
      try {
        await refreshPushSettingsState(setPush);
      } catch (error) {
        setPush({ supported: false, enabled: false, subscribed: false, busy: false, status: error.message || 'Notifications could not be checked.' });
      }
    }, []);

    React.useEffect(() => { refresh(); }, [refresh]);

    const enable = async () => {
      try {
        setPush((current) => ({ ...current, busy: true, status: 'Enabling notifications...' }));
        await subscribeForSettingsPush();
        await refresh();
        if (onToast) onToast('Thread notifications enabled');
      } catch (error) {
        await refresh();
        if (onError) onError(error);
      }
    };

    const disable = async () => {
      try {
        setPush((current) => ({ ...current, busy: true, status: 'Disabling notifications...' }));
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await window.API.unsubscribePush(subscription.endpoint);
          await subscription.unsubscribe();
        }
        await refresh();
        if (onToast) onToast('Thread notifications disabled');
      } catch (error) {
        await refresh();
        if (onError) onError(error);
      }
    };

    return (
      <div role="tabpanel" aria-label="Notifications">
        <div className="settings-block-head">
          <h3>Thread notifications</h3>
        </div>
        <div className="settings-list">
          <div className="settings-item settings-notification-item">
            <div className="settings-item-head">
              <Icon name="bell" size={15} />
              <div>
                <strong>Ready for review and attention alerts</strong>
                <div className="t-sub">{push.status}</div>
              </div>
              {push.supported && push.enabled && !push.subscribed && <button className="btn sm" data-appligator-id="settings-enable-thread-notifications" disabled={push.busy || Notification.permission === 'denied'} onClick={enable}><Icon name="bell" size={13} />Enable</button>}
              {push.supported && push.enabled && push.subscribed && <button className="btn sm ghost" data-appligator-id="settings-disable-thread-notifications" disabled={push.busy} onClick={disable}><Icon name="close" size={13} />Disable</button>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  async function refreshPushSettingsState(setPush) {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      setPush({ supported: false, enabled: false, subscribed: false, busy: false, status: 'Notifications are not supported by this browser.' });
      return;
    }
    const config = await window.API.getPushConfig();
    if (!config || !config.enabled || !config.publicKey) {
      setPush({ supported: true, enabled: false, subscribed: false, busy: false, status: 'Notifications are not configured on this server.' });
      return;
    }
    const registration = await navigator.serviceWorker.register('/app-push-sw.js?v=20260630-push');
    const subscription = await registration.pushManager.getSubscription();
    const permission = Notification.permission;
    setPush({
      supported: true,
      enabled: true,
      subscribed: !!subscription,
      busy: false,
      status: subscription
        ? 'This browser will notify you when threads are ready for review or need input.'
        : permission === 'denied'
          ? 'Notifications are blocked for this site in your browser settings.'
          : 'Enable notifications for this browser and device.'
    });
  }

  async function subscribeForSettingsPush() {
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Notification permission was not granted.');
      }
    }
    const config = await window.API.getPushConfig();
    if (!config || !config.enabled || !config.publicKey) {
      throw new Error('Notifications are not configured on this server.');
    }
    const registration = await navigator.serviceWorker.register('/app-push-sw.js?v=20260630-push');
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: base64UrlToUint8Array(config.publicKey)
    });
    const json = subscription.toJSON();
    await window.API.subscribePush({
      endpoint: json.endpoint,
      keys: json.keys,
      userAgent: navigator.userAgent
    });
  }

  function base64UrlToUint8Array(value) {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const bytes = new Uint8Array(raw.length);
    for (let index = 0; index < raw.length; index += 1) bytes[index] = raw.charCodeAt(index);
    return bytes;
  }

  function Settings({ db, session, onToast, onError, onSave, onReload }) {
    const s = db.settings;
    const [form, setForm] = React.useState({ ...s });
    const [emailFolders, setEmailFolders] = React.useState([]);
    const [coreSettingsSection, setCoreSettingsSection] = React.useState('profile');
    const [codexPluginErrors, setCodexPluginErrors] = React.useState({});
    const [codexCatalogQuery, setCodexCatalogQuery] = React.useState('');
    const [codexCatalogResults, setCodexCatalogResults] = React.useState([]);
    const [codexCatalogIssues, setCodexCatalogIssues] = React.useState([]);
    const [codexCatalogBusy, setCodexCatalogBusy] = React.useState(false);
    const [codexCapabilityDrawer, setCodexCapabilityDrawer] = React.useState(null);
    const [codexCapabilityForm, setCodexCapabilityForm] = React.useState(null);
    const [codexCapabilityDrawerError, setCodexCapabilityDrawerError] = React.useState('');
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const openAiTextModels = ['gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.4-nano'];
    const openAiVoiceModels = ['gpt-realtime-2', 'gpt-realtime-1.5', 'gpt-realtime', 'gpt-realtime-mini'];
    const codexModels = ['gpt-5.6-sol', 'gpt-5.6-terra', 'gpt-5.6-luna', 'gpt-5.5', 'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.3-codex-spark'];
    const codexReasoningOptions = ['low', 'medium', 'high'];
    const codexSpeedOptions = ['standard', 'fast'];
    const codexFastAvailable = !form.codexModel || ['gpt-5.5', 'gpt-5.4'].includes(form.codexModel);
    const codexPluginSourceKinds = [
      { value: 'InstalledMarketplace', label: 'Installed marketplace', sourceLabel: 'Marketplace name', placeholder: 'openai-curated' },
      { value: 'Local', label: 'Local marketplace', sourceLabel: 'Local marketplace root', placeholder: '/Users/name/codex-marketplace' },
      { value: 'GitHub', label: 'GitHub repo', sourceLabel: 'GitHub repo or skill URL', placeholder: 'owner/repo or https://github.com/owner/repo' },
      { value: 'GitUrl', label: 'Git URL', sourceLabel: 'Git URL', placeholder: 'https://github.com/org/repo.git or git@github.com:org/repo.git' },
    ];
    const codexPluginSourceMeta = (sourceKind) => codexPluginSourceKinds.find((item) => item.value === sourceKind) || codexPluginSourceKinds[0];
    const generateCodexToken = () => {
      const bytes = new Uint8Array(32);
      crypto.getRandomValues(bytes);
      set('codexAppServerToken', btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', ''));
    };
    const downloadCodexBridgeInstaller = () => window.open('/api/codex-bridge/install-script', '_blank', 'noreferrer');
    const copyText = async (text, label) => {
      await navigator.clipboard.writeText(text);
      onToast(`${label} copied`);
    };
    const copyBrowserExtensionToken = async () => {
      try {
        const config = await window.API.getBrowserExtensionConfig();
        await copyText(config.token || '', 'Extension token');
      } catch (error) {
        onError(error);
      }
    };
    const copyBrowserExtensionConfig = async () => {
      try {
        const config = await window.API.getBrowserExtensionConfig();
        await copyText(JSON.stringify(config, null, 2), 'Extension config');
      } catch (error) {
        onError(error);
      }
    };
    const codexCapabilities = form.codexCapabilities || { plugins: [], skills: [] };
    const setCodexCapabilities = (next) => set('codexCapabilities', next);
    const newCodexPlugin = () => ({ enabled: true, pluginName: '', sourceKind: 'InstalledMarketplace', marketplaceName: '', marketplaceSource: '', ref: '', sparsePaths: '' });
    const newCodexSkill = () => ({ enabled: true, name: '', sourceKind: 'Content', url: '', content: '' });
    const codexIdPattern = /^[A-Za-z0-9_~-]+$/;
    const extractMarkdownLink = (value) => {
      const clean = String(value || '').trim();
      const match = clean.match(/^\[[^\]]+\]\(([^)]+)\)$/);
      return match ? match[1].trim() : clean;
    };
    const normalizeGitHubSource = (value) => {
      let clean = extractMarkdownLink(value).trim();
      const rawMatch = clean.match(/^https:\/\/raw\.githubusercontent\.com\/([^/\s]+)\/([^/\s]+)\/[^/\s]+\/.*$/i);
      if (rawMatch) return `${rawMatch[1]}/${rawMatch[2].replace(/\.git$/i, '')}`;
      clean = clean.replace(/^https:\/\/github\.com\//i, '').replace(/^git@github\.com:/i, '').replace(/^\/+|\/+$/g, '');
      clean = clean.replace(/\/(?:blob|tree)\/.*$/i, '');
      return clean.toLowerCase().endsWith('.git') ? clean.slice(0, -4) : clean;
    };
    const parseGitHubSkillPaste = (value) => {
      const clean = extractMarkdownLink(value).trim();
      let match = clean.match(/^https:\/\/raw\.githubusercontent\.com\/([^/\s]+)\/([^/\s]+)\/([^/\s]+)\/(.+)$/i);
      if (match) return { source: `${match[1]}/${match[2].replace(/\.git$/i, '')}`, sparsePath: match[4] };
      match = clean.match(/^https:\/\/github\.com\/([^/\s]+)\/([^/\s]+)\/(?:blob|tree)\/([^/\s]+)\/(.+)$/i);
      if (match) return { source: `${match[1]}/${match[2].replace(/\.git$/i, '')}`, sparsePath: match[4] };
      return { source: normalizeGitHubSource(clean), sparsePath: '' };
    };
    const pluginIdFromSource = (value) => {
      const clean = normalizeGitHubSource(value).split(/[?#]/)[0].replace(/\/+$/g, '');
      const parts = clean.split('/');
      const repo = (parts.length >= 2 ? parts[1] : parts[0] || '').replace(/\.git$/i, '');
      return repo.replace(/-skill$/i, '').replace(/[^A-Za-z0-9_~-]+/g, '-').replace(/^-+|-+$/g, '');
    };
    const normalizeCodexPlugin = (plugin) => {
      const sourceKind = plugin.sourceKind || 'InstalledMarketplace';
      const next = {
        ...plugin,
        pluginName: String(plugin.pluginName || '').trim(),
        sourceKind,
        marketplaceName: String(plugin.marketplaceName || '').trim(),
        marketplaceSource: extractMarkdownLink(plugin.marketplaceSource),
        ref: String(plugin.ref || '').trim(),
        sparsePaths: String(plugin.sparsePaths || '').trim(),
      };
      if (sourceKind === 'GitHub') {
        next.marketplaceSource = normalizeGitHubSource(next.marketplaceSource);
      }
      if (sourceKind === 'InstalledMarketplace') {
        next.marketplaceSource = '';
        next.ref = '';
        next.sparsePaths = '';
      }
      if (sourceKind === 'Local') {
        next.ref = '';
        next.sparsePaths = '';
      }
      return next;
    };
    const validateCodexPlugin = (plugin) => {
      if (plugin.enabled === false) return '';
      const normalized = normalizeCodexPlugin(plugin);
      if (!normalized.pluginName) return 'Plugin ID is required.';
      if (!codexIdPattern.test(normalized.pluginName)) return 'Plugin ID must use the manifest ID, for example data-analytics. Spaces are not allowed.';
      if (normalized.sourceKind === 'InstalledMarketplace') {
        if (!normalized.marketplaceName) return 'Marketplace name is required.';
        if (!codexIdPattern.test(normalized.marketplaceName)) return 'Marketplace name can only contain letters, numbers, underscore, hyphen, or tilde.';
        return '';
      }
      if (!normalized.marketplaceSource) return 'Marketplace source is required.';
      if (normalized.marketplaceSource.includes('chatgpt.com/plugins/share/')) return 'ChatGPT share links cannot be installed here. Install the shared plugin in Codex, then use Installed marketplace.';
      if (normalized.sourceKind === 'GitHub' && !/^[^/\s]+\/[^/\s]+$/.test(normalized.marketplaceSource)) return 'GitHub repo must be owner/repo.';
      if (normalized.sourceKind === 'GitUrl' && !/^(https?:\/\/|git@|ssh:\/\/).+/.test(normalized.marketplaceSource)) return 'Git URL must be an HTTPS or SSH Git URL.';
      return '';
    };
    const validatedSettings = () => {
      const plugins = (codexCapabilities.plugins || []).map(normalizeCodexPlugin);
      const errors = {};
      plugins.forEach((plugin, index) => {
        const error = validateCodexPlugin(plugin);
        if (error) errors[index] = error;
      });
      return {
        errors,
        settings: {
          ...form,
          codexCapabilities: {
            ...codexCapabilities,
            plugins,
          },
          currentCashBalance: Number(form.currentCashBalance) || 0,
          paymentTermsDays: parseInt(form.paymentTermsDays || '0', 10),
        }
      };
    };
    const save = () => {
      const result = validatedSettings();
      setCodexPluginErrors(result.errors);
      if (Object.keys(result.errors).length > 0) {
        setCoreSettingsSection('codex');
        if (onError) onError(new Error('Fix the Codex plugin settings before saving.'));
        return;
      }
      if (onSave) onSave(result.settings);
      else onToast('Settings saved');
    };
    const removeCodexPlugin = (index) => {
      setCodexPluginErrors({});
      setCodexCapabilities({
        ...codexCapabilities,
        plugins: (codexCapabilities.plugins || []).filter((_, i) => i !== index),
      });
    };
    const removeCodexSkill = (index) => setCodexCapabilities({
      ...codexCapabilities,
      skills: (codexCapabilities.skills || []).filter((_, i) => i !== index),
    });
    const openCodexCapabilityDrawer = (type, row) => {
      setCodexCapabilityDrawerError('');
      if (row) {
        setCodexCapabilityDrawer({ mode: 'edit', type: row.type, index: row.index });
        setCodexCapabilityForm({ ...row.item });
        return;
      }
      setCodexCapabilityDrawer({ mode: 'create', type, index: null });
      setCodexCapabilityForm(type === 'plugin' ? newCodexPlugin() : newCodexSkill());
    };
    const closeCodexCapabilityDrawer = () => {
      setCodexCapabilityDrawer(null);
      setCodexCapabilityForm(null);
      setCodexCapabilityDrawerError('');
    };
    const setCodexCapabilityField = (key, value) => setCodexCapabilityForm((current) => ({ ...current, [key]: value }));
    const setCodexPluginSource = (value) => setCodexCapabilityForm((current) => {
      if (!current) return current;
      const next = { ...current, marketplaceSource: value };
      if ((current.sourceKind || 'InstalledMarketplace') === 'GitHub') {
        const parsed = parseGitHubSkillPaste(value);
        next.marketplaceSource = parsed.source;
        if (parsed.sparsePath && !String(current.sparsePaths || '').trim()) {
          next.sparsePaths = parsed.sparsePath;
        }
      }
      if (!String(current.pluginName || '').trim()) {
        const pluginId = pluginIdFromSource(value);
        if (pluginId) next.pluginName = pluginId;
      }
      return next;
    });
    const saveCodexCapability = () => {
      if (!codexCapabilityDrawer || !codexCapabilityForm) return;
      if (codexCapabilityDrawer.type === 'plugin') {
        const plugin = normalizeCodexPlugin(codexCapabilityForm);
        const error = validateCodexPlugin(plugin);
        if (error) {
          setCodexCapabilityDrawerError(error);
          return;
        }
        const plugins = codexCapabilityDrawer.mode === 'edit'
          ? (codexCapabilities.plugins || []).map((item, i) => i === codexCapabilityDrawer.index ? plugin : item)
          : [...(codexCapabilities.plugins || []), plugin];
        setCodexPluginErrors({});
        setCodexCapabilities({ ...codexCapabilities, plugins });
        closeCodexCapabilityDrawer();
        return;
      }
      const skill = { ...codexCapabilityForm, name: String(codexCapabilityForm.name || '').trim(), sourceKind: codexCapabilityForm.sourceKind || 'Content' };
      const skills = codexCapabilityDrawer.mode === 'edit'
        ? (codexCapabilities.skills || []).map((item, i) => i === codexCapabilityDrawer.index ? skill : item)
        : [...(codexCapabilities.skills || []), skill];
      setCodexCapabilities({ ...codexCapabilities, skills });
      closeCodexCapabilityDrawer();
    };
    const updateCodexCapabilitySourceKind = (sourceKind) => {
      if (!codexCapabilityDrawer || !codexCapabilityForm) return;
      if (codexCapabilityDrawer.type === 'plugin') {
        setCodexCapabilityForm({ ...codexCapabilityForm, sourceKind, marketplaceName: '', marketplaceSource: '', ref: '', sparsePaths: '' });
        return;
      }
      setCodexCapabilityField('sourceKind', sourceKind);
    };
    const removeCodexCapability = (row) => {
      const label = row.type === 'plugin' ? 'plugin' : 'skill';
      if (!window.confirm(`Remove ${row.name || `this ${label}`}? Save settings to persist this change.`)) return;
      row.type === 'plugin' ? removeCodexPlugin(row.index) : removeCodexSkill(row.index);
      if (onToast) onToast(`Removed ${row.name || label}. Save settings to persist.`);
    };
    const codexPluginKey = (plugin) => {
      const normalized = normalizeCodexPlugin(plugin || {});
      return [normalized.pluginName, normalized.sourceKind, normalized.marketplaceName, normalized.marketplaceSource, normalized.ref].join('|').toLowerCase();
    };
    const codexSkillKey = (skill) => [skill.name || '', skill.sourceKind || 'Content', skill.url || ''].join('|').toLowerCase();
    const hasCodexPlugin = (plugin) => (codexCapabilities.plugins || []).some((item) => codexPluginKey(item) === codexPluginKey(plugin));
    const hasCodexSkill = (skill) => (codexCapabilities.skills || []).some((item) => codexSkillKey(item) === codexSkillKey(skill));
    const codexPluginSourceText = (plugin) => {
      const sourceKind = plugin.sourceKind || 'InstalledMarketplace';
      if (sourceKind === 'InstalledMarketplace') return plugin.marketplaceName || 'Marketplace';
      if (sourceKind === 'Local') return plugin.marketplaceSource || 'Local marketplace';
      if (sourceKind === 'GitHub') return plugin.marketplaceSource || 'GitHub repo';
      return plugin.marketplaceSource || 'Git URL';
    };
    const codexSkillSourceText = (skill) => (skill.sourceKind || 'Content') === 'Url' ? (skill.url || 'URL') : 'Inline content';
    const codexCapabilityRows = [
      ...(codexCapabilities.plugins || []).map((plugin, index) => ({
        type: 'plugin',
        typeLabel: 'Plugin',
        index,
        item: plugin,
        name: plugin.pluginName || 'Untitled plugin',
        sourceKind: plugin.sourceKind || 'InstalledMarketplace',
        source: codexPluginSourceText(plugin),
        enabled: plugin.enabled !== false,
        error: codexPluginErrors[index] || validateCodexPlugin(plugin),
      })),
      ...(codexCapabilities.skills || []).map((skill, index) => ({
        type: 'skill',
        typeLabel: 'Skill',
        index,
        item: skill,
        name: skill.name || 'Untitled skill',
        sourceKind: skill.sourceKind || 'Content',
        source: codexSkillSourceText(skill),
        enabled: skill.enabled !== false,
        error: '',
      })),
    ];
    const addCodexCatalogItem = (item) => {
      if (item.kind === 'Plugin' && item.plugin) {
        const plugin = normalizeCodexPlugin({ ...item.plugin, enabled: true });
        if (hasCodexPlugin(plugin)) {
          onToast('Plugin already added');
          return;
        }
        setCodexCapabilities({ ...codexCapabilities, plugins: [...(codexCapabilities.plugins || []), plugin] });
        onToast(`Added ${plugin.pluginName}`);
        return;
      }
      if (item.kind === 'Skill' && item.skill) {
        const skill = { ...item.skill, enabled: true, sourceKind: item.skill.sourceKind || 'Url' };
        if (hasCodexSkill(skill)) {
          onToast('Skill already added');
          return;
        }
        setCodexCapabilities({ ...codexCapabilities, skills: [...(codexCapabilities.skills || []), skill] });
        onToast(`Added ${skill.name}`);
      }
    };
    const addAllCodexCatalogItems = () => {
      const plugins = [...(codexCapabilities.plugins || [])];
      const skills = [...(codexCapabilities.skills || [])];
      const pluginKeys = new Set(plugins.map(codexPluginKey));
      const skillKeys = new Set(skills.map(codexSkillKey));
      let added = 0;
      codexCatalogResults.forEach((item) => {
        if (item.kind === 'Plugin' && item.plugin) {
          const plugin = normalizeCodexPlugin({ ...item.plugin, enabled: true });
          const key = codexPluginKey(plugin);
          if (!pluginKeys.has(key)) {
            plugins.push(plugin);
            pluginKeys.add(key);
            added += 1;
          }
          return;
        }
        if (item.kind === 'Skill' && item.skill) {
          const skill = { ...item.skill, enabled: true, sourceKind: item.skill.sourceKind || 'Url' };
          const key = codexSkillKey(skill);
          if (!skillKeys.has(key)) {
            skills.push(skill);
            skillKeys.add(key);
            added += 1;
          }
        }
      });
      if (added === 0) {
        onToast('Catalog items already added');
        return;
      }
      setCodexCapabilities({ ...codexCapabilities, plugins, skills });
      onToast(`Added ${added} Codex requirement${added === 1 ? '' : 's'}`);
    };
    const searchCodexCatalog = async () => {
      setCodexCatalogBusy(true);
      setCodexCatalogIssues([]);
      try {
        const result = await window.API.searchCodexCatalog({ query: codexCatalogQuery, sources: form.codexCapabilityCatalogUrls || '' });
        setCodexCatalogResults(result.results || []);
        setCodexCatalogIssues(result.issues || []);
      } catch (error) {
        if (onError) onError(error);
      } finally {
        setCodexCatalogBusy(false);
      }
    };
    const testEmail = async () => {
      try {
        const result = await window.API.testEmailConnection();
        onToast(`Email connected. Inbox: ${result.inboxCount}`);
      } catch (error) {
        onError(error);
      }
    };
    const loadEmailFolders = async () => {
      try {
        const folders = await window.API.listEmailFolders();
        setEmailFolders(folders);
        const sent = folders.find((folder) => folder.isSentCandidate && folder.selectable) || folders.find((folder) => folder.selectable);
        if (!form.emailSentFolder && sent) set('emailSentFolder', sent.fullName);
        onToast(`${folders.length} email folders found`);
      } catch (error) {
        onError(error);
      }
    };
    const isAdmin = session && session.user && session.user.role === "Admin";
    const canManageOrganizations = isAdmin && session.organization && session.organization.slug === 'appligator';
    const settingsSections = [
      { id: 'profile', label: 'Profile', icon: 'customers', title: 'Company profile', sub: 'Appears on invoices and statements' },
      { id: 'billing', label: 'Billing', icon: 'invoices', title: 'Billing defaults', sub: 'Currency, payment terms, invoice numbering' },
      { id: 'notifications', label: 'Notifications', icon: 'bell', title: 'Notifications', sub: 'Thread alerts for this browser and device' },
      ...(isAdmin ? [
        { id: 'designmd', label: 'DesignMD', icon: 'doc', title: 'DesignMD', sub: 'External design generation API' },
        { id: 'openai', label: 'OpenAI', icon: 'server', title: 'OpenAI', sub: 'ChatGPT API settings' },
        { id: 'codex', label: 'Codex', icon: 'server', title: 'Codex', sub: 'Catalog, plugins, skills, and local bridge settings' },
        { id: 'storage', label: 'Storage', icon: 'link', title: 'Storage accounts', sub: 'Authorize cloud storage for external document sync' },
        { id: 'email', label: 'Email', icon: 'mail', title: 'Email account', sub: 'Backend mailbox access' },
        { id: 'users', label: 'Users', icon: 'users', title: 'Users', sub: 'Application access' },
        ...(canManageOrganizations ? [{ id: 'organizations', label: 'Orgs', icon: 'shield', title: 'Organizations', sub: 'Independent company workspaces' }] : []),
      ] : []),
    ];
    const activeSettingsSection = settingsSections.some((section) => section.id === coreSettingsSection) ? coreSettingsSection : 'profile';
    const activeSettingsMeta = settingsSections.find((section) => section.id === activeSettingsSection) || settingsSections[0];
    return (
      <div style={{ maxWidth: 1040 }}>
        <Panel
          title={activeSettingsMeta.title}
          sub={activeSettingsMeta.sub}
          actions={!['notifications', 'users', 'organizations'].includes(activeSettingsSection) && <button className="btn" onClick={save}><Icon name="check" size={14} />Save settings</button>}
        >
          <div className="settings-split">
            <div className="settings-rail" role="tablist" aria-label="Settings sections">
              {settingsSections.map((section) => (
                <button key={section.id} className={activeSettingsSection === section.id ? 'on' : ''} role="tab" aria-selected={activeSettingsSection === section.id} onClick={() => setCoreSettingsSection(section.id)}>
                  <Icon name={section.icon} size={14} />
                  <span>{section.label}</span>
                </button>
              ))}
            </div>
            <div className="settings-section">
              {activeSettingsSection === 'profile' && (
                <div role="tabpanel" aria-label="Company profile">
                  <div className="field"><label>Company name</label><input className="inp" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} /></div>
                  <div className="field-row">
                    <div className="field"><label>Legal name</label><input className="inp" value={form.legalName} onChange={(e) => set('legalName', e.target.value)} /></div>
                    <div className="field"><label>Tax ID</label><input className="inp" value={form.taxId} onChange={(e) => set('taxId', e.target.value)} /></div>
                  </div>
                  <div className="field"><label>Address</label><textarea className="inp" value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
                  <div className="field"><label>Bank details</label><textarea className="inp" value={form.bankDetails} onChange={(e) => set('bankDetails', e.target.value)} /></div>
                </div>
              )}
              {activeSettingsSection === 'billing' && (
                <div role="tabpanel" aria-label="Billing defaults">
                  <div className="field-row">
                    <div className="field"><label>Currency</label><input className="inp" value={form.currency} onChange={(e) => set('currency', e.target.value)} maxLength={3} /></div>
                    <div className="field"><label>Payment terms (days)</label><input className="inp num" type="number" value={form.paymentTermsDays} onChange={(e) => set('paymentTermsDays', e.target.value)} /></div>
                  </div>
                  <div className="field"><label>Opening cash balance</label><input className="inp num" type="number" value={form.currentCashBalance} onChange={(e) => set('currentCashBalance', e.target.value)} /></div>
                  <div className="field"><label>Next invoice number</label><input className="inp num" value={form.nextInvoiceNumber} onChange={(e) => set('nextInvoiceNumber', e.target.value)} /></div>
                  <div className="divider" />
                  <dl className="detail-grid">
                    <dt>Database</dt><dd className="mono">company-data.db</dd>
                    <dt>Documents</dt><dd className="mono">/expense-documents</dd>
                  </dl>
                </div>
              )}
              {activeSettingsSection === 'notifications' && (
                <NotificationSettings onToast={onToast} onError={onError} />
              )}
              {activeSettingsSection === 'designmd' && (
                <div role="tabpanel" aria-label="DesignMD">
                  <div className="field"><label>API URL</label><input className="inp" value={form.designMdApiUrl || ''} onChange={(e) => set('designMdApiUrl', e.target.value)} placeholder="https://designmd.ai/api/v1" /></div>
                  <div className="field"><label>API key</label><input className="inp" type="password" value={form.designMdApiKey || ''} onChange={(e) => set('designMdApiKey', e.target.value)} placeholder="Leave unchanged" /></div>
                </div>
              )}
              {activeSettingsSection === 'openai' && (
                <div role="tabpanel" aria-label="OpenAI">
                  <div className="field"><label>API key</label><input className="inp" type="password" value={form.openAiApiKey || ''} onChange={(e) => set('openAiApiKey', e.target.value)} placeholder="Leave unchanged" /></div>
                  <div className="field-row">
                    <div className="field"><label>Text model</label><select className="inp" value={form.openAiTextModel || 'gpt-5.5'} onChange={(e) => set('openAiTextModel', e.target.value)}>
                      {!openAiTextModels.includes(form.openAiTextModel || '') && form.openAiTextModel && <option value={form.openAiTextModel}>{form.openAiTextModel}</option>}
                      {openAiTextModels.map((model) => <option key={model} value={model}>{model}</option>)}
                    </select></div>
                    <div className="field"><label>Voice model</label><select className="inp" value={form.openAiVoiceModel || 'gpt-realtime-2'} onChange={(e) => set('openAiVoiceModel', e.target.value)}>
                      {!openAiVoiceModels.includes(form.openAiVoiceModel || '') && form.openAiVoiceModel && <option value={form.openAiVoiceModel}>{form.openAiVoiceModel}</option>}
                      {openAiVoiceModels.map((model) => <option key={model} value={model}>{model}</option>)}
                    </select></div>
                  </div>
                </div>
              )}
              {activeSettingsSection === 'codex' && (
                <div role="tabpanel" aria-label="Codex">
                  <div className="codex-settings-grid">
                    <div className="codex-settings-main">
                      <div className="settings-block-head">
                        <h3>Companion</h3>
                      </div>
                      <div className="field"><label>Bearer token</label><input className="inp" type="password" value={form.codexAppServerToken || ''} onChange={(e) => set('codexAppServerToken', e.target.value)} placeholder="Leave unchanged" /></div>
                      <div className="field-row">
                        <div className="field"><label>Local listen URL</label><input className="inp" value={form.codexBridgeLocalUrl || 'ws://127.0.0.1:4500'} onChange={(e) => set('codexBridgeLocalUrl', e.target.value)} placeholder="ws://127.0.0.1:4500" /></div>
                        <div className="field"><label>Codex binary</label><input className="inp" value={form.codexBridgeCodexBinary || 'codex'} onChange={(e) => set('codexBridgeCodexBinary', e.target.value)} placeholder="codex" /></div>
                      </div>
                      <div className="settings-block-head">
                        <h3>Thread defaults</h3>
                      </div>
                      <div className="field-row">
                        <div className="field"><label>Model</label><select className="inp" value={form.codexModel || ''} onChange={(e) => set('codexModel', e.target.value)} data-appligator-id="settings-codex-model">
                          <option value="">Codex default</option>
                          {codexModels.map((model) => <option key={model} value={model}>{model}</option>)}
                        </select></div>
                        <div className="field"><label>Reasoning</label><select className="inp" value={form.codexReasoning || ''} onChange={(e) => set('codexReasoning', e.target.value)} data-appligator-id="settings-codex-reasoning">
                          <option value="">Codex default</option>
                          {codexReasoningOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                        </select></div>
                        <div className="field"><label>Speed</label><select className="inp" value={form.codexSpeed || ''} onChange={(e) => set('codexSpeed', e.target.value)} data-appligator-id="settings-codex-speed">
                          <option value="">Codex default</option>
                          {codexSpeedOptions.map((option) => <option key={option} value={option} disabled={option === 'fast' && !codexFastAvailable}>{option}</option>)}
                        </select></div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button className="btn ghost" onClick={generateCodexToken}><Icon name="refresh" size={14} />Generate token</button>
                        <button className="btn ghost" onClick={copyBrowserExtensionToken}><Icon name="copy" size={14} />Copy Extension Token</button>
                        <button className="btn ghost" onClick={copyBrowserExtensionConfig}><Icon name="copy" size={14} />Copy Extension Config</button>
                        <button className="btn ghost" onClick={downloadCodexBridgeInstaller}><Icon name="download" size={14} />Download Companion</button>
                      </div>
                      <div className="hint">Browser extension reinstall actions are available from the Appligator Companion menu bar icon.</div>
                      <div className="divider" />
                      <div className="settings-block-head">
                        <h3>Catalog</h3>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {codexCatalogResults.length > 0 && <button className="btn ghost sm" onClick={addAllCodexCatalogItems}><Icon name="plus" size={13} />Add all</button>}
                          <button className="btn ghost sm" onClick={searchCodexCatalog} disabled={codexCatalogBusy}><Icon name="search" size={13} />{codexCatalogBusy ? 'Searching...' : 'Search'}</button>
                        </div>
                      </div>
                      <div className="field"><label>Catalog sources</label><textarea className="inp settings-catalog-sources" value={form.codexCapabilityCatalogUrls || ''} onChange={(e) => set('codexCapabilityCatalogUrls', e.target.value)} placeholder={'https://raw.githubusercontent.com/openai/role-specific-plugins/main/.agents/plugins/marketplace.json\nhttps://github.com/addyosmani/agent-skills/tree/main/skills\nhttps://example.com/codex-catalog.json'} /></div>
                      <div className="field"><label>Search plugins and skills</label><input className="inp" value={codexCatalogQuery} onChange={(e) => setCodexCatalogQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') searchCodexCatalog(); }} placeholder="data, design, GitHub..." /></div>
                      {codexCatalogIssues.map((issue) => <div className="settings-validation" key={issue.source}>{issue.source}: {issue.error}</div>)}
                      {codexCatalogResults.length > 0 && (
                        <div className="settings-list codex-catalog-results">
                          {codexCatalogResults.map((item) => {
                            const added = item.kind === 'Plugin' ? hasCodexPlugin(item.plugin) : hasCodexSkill(item.skill);
                            return (
                              <div className="settings-item codex-catalog-result" key={`${item.kind}-${item.sourceName}-${item.name}`}>
                                <div>
                                  <div className="codex-catalog-title"><strong>{item.name}</strong><span>{item.kind}</span>{item.category && <span>{item.category}</span>}</div>
                                  <div className="hint">{item.description || item.sourceName}</div>
                                </div>
                                <button className="btn ghost sm" onClick={() => addCodexCatalogItem(item)} disabled={added}><Icon name={added ? 'check' : 'plus'} size={13} />{added ? 'Added' : 'Add'}</button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {codexCatalogResults.length === 0 && codexCatalogQuery && !codexCatalogBusy && <div className="settings-empty">No catalog matches.</div>}
                    </div>
                    <div className="codex-settings-requirements">
                      <div className="settings-block-head">
                        <h3>Requirements</h3>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button className="btn ghost sm" onClick={() => openCodexCapabilityDrawer('plugin')}><Icon name="plus" size={13} />Add plugin</button>
                          <button className="btn ghost sm" onClick={() => openCodexCapabilityDrawer('skill')}><Icon name="plus" size={13} />Add skill</button>
                        </div>
                      </div>
                      <DataTable
                        rows={codexCapabilityRows}
                        rowKey={(row) => `${row.type}-${row.index}`}
                        empty="No Codex plugin or skill requirements."
                        columns={[
                          { header: 'Type', render: (row) => <Pill kind={row.type === 'plugin' ? 'Open' : 'Draft'} sm>{row.typeLabel}</Pill> },
                          { header: 'Requirement', render: (row) => <span className="cellstack"><span className="t-strong">{row.name}</span>{row.error && <span className="neg" style={{ fontSize: 11 }}>{row.error}</span>}</span> },
                          { header: 'Source', render: (row) => <span className="cellstack"><span>{row.source}</span><span className="t-sub">{row.sourceKind}</span></span> },
                          { header: 'Status', render: (row) => <Pill kind={row.enabled ? 'Active' : 'Inactive'} sm /> },
                        ]}
                        actions={[
                          { icon: 'edit', title: 'Edit', onClick: (row) => openCodexCapabilityDrawer(row.type, row) },
                          { icon: 'trash', title: 'Remove', onClick: removeCodexCapability, className: 'icon-btn danger' },
                        ]}
                      />
                    </div>
                  </div>
                  {codexCapabilityDrawer && codexCapabilityForm && <React.Fragment>
                    <div className="overlay" onClick={closeCodexCapabilityDrawer} />
                    <aside className="drawer">
                      <header>
                        <div style={{ flex: 1 }}>
                          <h2>{codexCapabilityDrawer.mode === 'edit' ? 'Edit' : 'Create'} {codexCapabilityDrawer.type}</h2>
                          <div className="sub">Codex {codexCapabilityDrawer.type === 'plugin' ? 'plugin' : 'skill'} requirement</div>
                        </div>
                        <button className="icon-btn" onClick={closeCodexCapabilityDrawer}><Icon name="close" size={17} /></button>
                      </header>
                      <div className="body">
                        <label className="checkrow"><input type="checkbox" checked={codexCapabilityForm.enabled !== false} onChange={(e) => setCodexCapabilityField('enabled', e.target.checked)} /> Enabled</label>
                        {codexCapabilityDrawer.type === 'plugin' ? (() => {
                          const sourceKind = codexCapabilityForm.sourceKind || 'InstalledMarketplace';
                          const sourceMeta = codexPluginSourceMeta(sourceKind);
                          const isInstalledMarketplace = sourceKind === 'InstalledMarketplace';
                          const isGitSource = sourceKind === 'GitHub' || sourceKind === 'GitUrl';
                          const isShareLink = !isInstalledMarketplace && String(codexCapabilityForm.marketplaceSource || '').includes('chatgpt.com/plugins/share/');
                          return (
                            <React.Fragment>
                              <div className="field"><label>Source type</label><select className="inp" value={sourceKind} onChange={(e) => updateCodexCapabilitySourceKind(e.target.value)}>
                                {codexPluginSourceKinds.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                              </select></div>
                              <div className="field"><label>Plugin ID</label><input className="inp" value={codexCapabilityForm.pluginName || ''} onChange={(e) => setCodexCapabilityField('pluginName', e.target.value)} placeholder="data-analytics" /></div>
                              <div className="field"><label>{sourceMeta.sourceLabel}</label><input className="inp" value={isInstalledMarketplace ? (codexCapabilityForm.marketplaceName || '') : (codexCapabilityForm.marketplaceSource || '')} onChange={(e) => isInstalledMarketplace ? setCodexCapabilityField('marketplaceName', e.target.value) : setCodexPluginSource(e.target.value)} placeholder={sourceMeta.placeholder} /></div>
                              {isGitSource && (
                                <React.Fragment>
                                  <div className="field"><label>Ref</label><input className="inp" value={codexCapabilityForm.ref || ''} onChange={(e) => setCodexCapabilityField('ref', e.target.value)} placeholder="main, tag, or commit SHA" /></div>
                                  <div className="field"><label>Sparse paths</label><input className="inp" value={codexCapabilityForm.sparsePaths || ''} onChange={(e) => setCodexCapabilityField('sparsePaths', e.target.value)} placeholder="Optional, comma-separated" /></div>
                                </React.Fragment>
                              )}
                              {isShareLink && <div className="settings-validation">ChatGPT share links cannot be installed here. Install the shared plugin in Codex, then use Installed marketplace.</div>}
                            </React.Fragment>
                          );
                        })() : (
                          <React.Fragment>
                            <div className="field"><label>Source type</label><select className="inp" value={codexCapabilityForm.sourceKind || 'Content'} onChange={(e) => updateCodexCapabilitySourceKind(e.target.value)}>
                              <option value="Content">Content</option>
                              <option value="Url">URL</option>
                            </select></div>
                            <div className="field"><label>Skill name</label><input className="inp" value={codexCapabilityForm.name || ''} onChange={(e) => setCodexCapabilityField('name', e.target.value)} placeholder="work-package-implementation" /></div>
                            {(codexCapabilityForm.sourceKind || 'Content') === 'Url' ? (
                              <div className="field"><label>Skill URL</label><input className="inp" value={codexCapabilityForm.url || ''} onChange={(e) => setCodexCapabilityField('url', e.target.value)} placeholder="https://example.com/SKILL.md" /></div>
                            ) : (
                              <div className="field"><label>SKILL.md content</label><textarea className="inp settings-skill-content" value={codexCapabilityForm.content || ''} onChange={(e) => setCodexCapabilityField('content', e.target.value)} placeholder={'---\nname: work-package-implementation\ndescription: ...\n---\n\nInstructions...'} /></div>
                            )}
                          </React.Fragment>
                        )}
                        {codexCapabilityDrawerError && <div className="settings-validation">{codexCapabilityDrawerError}</div>}
                      </div>
                      <footer>
                        <button className="btn ghost" onClick={closeCodexCapabilityDrawer}>Cancel</button>
                        <button className="btn" onClick={saveCodexCapability}><Icon name="check" size={14} />{codexCapabilityDrawer.mode === 'edit' ? 'Save changes' : 'Create'}</button>
                      </footer>
                    </aside>
                  </React.Fragment>}
                </div>
              )}
              {activeSettingsSection === 'email' && (
                <div role="tabpanel" aria-label="Email account">
                  <label className="checkrow"><input type="checkbox" checked={!!form.emailEnabled} onChange={(e) => set('emailEnabled', e.target.checked)} />Enable backend email access</label>
                  <div className="field-row">
                    <div className="field"><label>Email address</label><input className="inp" value={form.emailAddress || ''} onChange={(e) => set('emailAddress', e.target.value)} placeholder="info@appligator.de" /></div>
                    <div className="field"><label>Username</label><input className="inp" value={form.emailUserName || ''} onChange={(e) => set('emailUserName', e.target.value)} placeholder="info@appligator.de" /></div>
                  </div>
                  <div className="field"><label>Password</label><input className="inp" type="password" value={form.emailPassword || ''} onChange={(e) => set('emailPassword', e.target.value)} placeholder="Leave unchanged" /></div>
                  <div className="field-row">
                    <div className="field"><label>IMAP host</label><input className="inp" value={form.emailImapHost || ''} onChange={(e) => set('emailImapHost', e.target.value)} placeholder="imap.goneo.de" /></div>
                    <div className="field"><label>IMAP port</label><input className="inp num" type="number" value={form.emailImapPort || 993} onChange={(e) => set('emailImapPort', parseInt(e.target.value || '993', 10))} /></div>
                  </div>
                  <label className="checkrow"><input type="checkbox" checked={form.emailImapUseSsl !== false} onChange={(e) => set('emailImapUseSsl', e.target.checked)} />Use SSL for IMAP</label>
                  <div className="field-row">
                    <div className="field"><label>SMTP host</label><input className="inp" value={form.emailSmtpHost || ''} onChange={(e) => set('emailSmtpHost', e.target.value)} placeholder="smtp.goneo.de" /></div>
                    <div className="field"><label>SMTP port</label><input className="inp num" type="number" value={form.emailSmtpPort || 465} onChange={(e) => set('emailSmtpPort', parseInt(e.target.value || '465', 10))} /></div>
                  </div>
                  <div className="field"><label>SMTP security</label><select className="inp" value={form.emailSmtpSecurity || 'SslOnConnect'} onChange={(e) => set('emailSmtpSecurity', e.target.value)}>
                    <option value="SslOnConnect">SSL on connect</option>
                    <option value="StartTls">STARTTLS</option>
                    <option value="StartTlsWhenAvailable">STARTTLS when available</option>
                    <option value="None">None</option>
                  </select></div>
                  <div className="field"><label>Sent folder</label><select className="inp" value={form.emailSentFolder || ''} onChange={(e) => set('emailSentFolder', e.target.value)}>
                    <option value="">Auto-detect</option>
                    {form.emailSentFolder && !emailFolders.some((folder) => folder.fullName === form.emailSentFolder) && <option value={form.emailSentFolder}>{form.emailSentFolder}</option>}
                    {emailFolders.filter((folder) => folder.selectable).map((folder) => <option key={folder.fullName} value={folder.fullName}>{folder.isSentCandidate ? 'Sent: ' : ''}{folder.fullName}</option>)}
                  </select></div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button className="btn ghost" onClick={testEmail}><Icon name="mail" size={14} />Test email</button>
                    <button className="btn ghost" onClick={loadEmailFolders}><Icon name="refresh" size={14} />Load folders</button>
                  </div>
                </div>
              )}
              {activeSettingsSection === 'storage' && (
                <StorageSettings db={db} form={form} set={set} onToast={onToast} onError={onError} onReload={onReload} />
              )}
              {activeSettingsSection === 'users' && (
                <div role="tabpanel" aria-label="Users">
                  <UsersSettings employees={db.employees} onToast={onToast} onError={onError} />
                </div>
              )}
              {activeSettingsSection === 'organizations' && (
                <div role="tabpanel" aria-label="Organizations">
                  <OrganizationSettings onToast={onToast} onError={onError} />
                </div>
              )}
            </div>
          </div>
        </Panel>
      </div>
    );
  }

  function EmailExplorer({ db, query, onToast, onError, onReload, onUnreadRefresh, onDrawer, onAction, scopeId, scopeProject, scopeCustomer, showProjectDrop = true }) {
    const [messages, setMessages] = React.useState([]);
    const [threadMessages, setThreadMessages] = React.useState(null);
    const [draftMessages, setDraftMessages] = React.useState([]);
    const [scopedDrafts, setScopedDrafts] = React.useState([]);
    const [selectedKey, setSelectedKey] = React.useState(null);
    const [selected, setSelected] = React.useState(null);
    const [emailStatusOverrides, setEmailStatusOverrides] = React.useState(() => new Map());
    const [loading, setLoading] = React.useState(false);
    const [compose, setCompose] = React.useState(null);
    const [documentPickerOpen, setDocumentPickerOpen] = React.useState(false);
    const [documentProjectFilter, setDocumentProjectFilter] = React.useState('');
    const [documentSearch, setDocumentSearch] = React.useState('');
    const [attachingDocumentKey, setAttachingDocumentKey] = React.useState('');
    const [sending, setSending] = React.useState(false);
    const [sentConfirmation, setSentConfirmation] = React.useState("");
    const [projectsOpen, setProjectsOpen] = React.useState(false);
    const [folder, setFolder] = React.useState('inbox');
    const [localSearch, setLocalSearch] = React.useState('');
    const [workflowOpen, setWorkflowOpen] = React.useState(false);
    const [threadOpen, setThreadOpen] = React.useState(false);
    const [starredKeys, setStarredKeys] = React.useState(() => new Set());
    const [archivedKeys, setArchivedKeys] = React.useState(() => new Set());
    const [draggedMessage, setDraggedMessage] = React.useState(null);
    const [dropTargetProjectId, setDropTargetProjectId] = React.useState(null);
    const account = (db.settings && db.settings.emailAddress) || '';
    const backendConfigured = db.settings && db.settings.emailBackendConfigured;
    const scoped = !!scopeId || !!scopeCustomer;
    const inThreadView = !scoped && threadMessages !== null;
    const scopeCustomerId = scopeCustomer && scopeCustomer.id;
    const projects = React.useMemo(() => [...(db.projects || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')), [db.projects]);
    const projectById = React.useMemo(() => new Map(projects.map((project) => [project.id, project])), [projects]);
    const documentRows = React.useMemo(() => projects.flatMap((project) => (project.documents || []).map((document) => ({
      key: `${project.id}:${document.id}`,
      project,
      document,
      canAttach: !!document.storedFileName && !document.externalUrl
    }))), [projects]);
    const projectEmailLinks = React.useMemo(() => projects.flatMap((project) => (project.emails || []).map((email) => ({
      project,
      id: email.id,
      sourceEmailId: String(email.sourceEmailId || '').toLowerCase(),
      status: email.status || 'unprocessed',
      isRequirement: !!email.isRequirement
    }))), [projects]);
    const projectEmailLinkFor = React.useCallback((message) => {
      if (!message) return null;
      const sourceEmailId = String(message.sourceEmailId || message.emailId || message.id || '').toLowerCase();
      return projectEmailLinks.find((item) => item.sourceEmailId && sourceEmailId && item.sourceEmailId === sourceEmailId) || null;
    }, [projectEmailLinks]);
    const connectedProjectFor = React.useCallback((message) => {
      if (!message) return null;
      if (message.projectId && projectById.has(message.projectId)) return projectById.get(message.projectId);
      const link = projectEmailLinkFor(message);
      return link ? link.project : null;
    }, [projectById, projectEmailLinkFor]);
    const contactEmailOptions = React.useMemo(() => {
      const seen = new Set();
      return (db.customers || []).flatMap((customer) => (customer.contactPersons || []).map((contact) => {
        const email = String(contact.email || '').trim();
        if (!email || seen.has(email.toLowerCase())) return null;
        seen.add(email.toLowerCase());
        const name = [contact.name, contact.lastName].filter(Boolean).join(' ');
        return { email, label: [name, customer.name].filter(Boolean).join(' · ') };
      }).filter(Boolean));
    }, [db.customers]);
    const scopedEmails = React.useMemo(() => {
      const project = scopeId ? (scopeProject || projectById.get(scopeId)) : null;
      return (project ? (project.emails || []) : []).map((message) => ({
        ...message,
        itemType: 'email',
        key: `email:${message.sourceEmailId || message.id}`,
        folderName: message.folderName || message.folder || 'Email',
        date: message.date || new Date().toISOString(),
        attachments: [],
        isDraft: false,
        status: emailStatusOverrides.get(`project:${project.id}:${message.id}`) || emailStatusOverrides.get(`email:${message.sourceEmailId || message.id}`) || message.status || 'unprocessed',
        projectEmailId: message.id,
        projectId: project.id,
        connectedProject: project
      }));
    }, [emailStatusOverrides, projectById, scopeId, scopeProject]);
    const scopedDraftMessages = React.useMemo(() => scopedDrafts.map((message) => ({
      ...message,
      itemType: 'email',
      key: `email:${message.id}`,
      folderName: message.folderName || 'Draft',
      date: message.date || new Date().toISOString(),
      attachments: message.attachments || [],
      isDraft: true
    })), [scopedDrafts]);
    const sourceMessages = React.useMemo(() => scoped
      ? [...scopedDraftMessages, ...scopedEmails].map((message) => ({ ...message, connectedProject: message.connectedProject || connectedProjectFor(message) })).sort((a, b) => new Date(b.date) - new Date(a.date))
      : [...draftMessages, ...messages].map((message) => {
        const link = projectEmailLinkFor(message);
        const override = emailStatusOverrides.get(`email:${message.id}`) || (link ? emailStatusOverrides.get(`project:${link.project.id}:${link.id}`) : '');
        return {
          ...message,
          itemType: 'email',
          key: `email:${message.id}`,
          connectedProject: connectedProjectFor(message),
          projectEmailId: link ? link.id : message.projectEmailId,
          status: override || (link ? link.status : message.status),
          isRequirement: link ? link.isRequirement : message.isRequirement
        };
      }),
      [connectedProjectFor, draftMessages, emailStatusOverrides, messages, projectEmailLinkFor, scoped, scopedDraftMessages, scopedEmails]);
    const projectLabel = (project) => project ? (project.name || 'Untitled project') : 'No project';
    const enrichedMessages = React.useMemo(() => sourceMessages.map((message) => ({
      ...message,
      isStarred: !!message.isStarred || starredKeys.has(message.key),
      isArchived: !!message.isArchived || archivedKeys.has(message.key)
    })), [archivedKeys, sourceMessages, starredKeys]);
    const messageFolder = (message) => {
      const raw = String(message.folder || message.folderName || '').toLowerCase();
      if (message.isArchived || raw.includes('archive')) return 'archive';
      if (message.isDraft || raw.includes('draft')) return 'drafts';
      if (raw.includes('sent')) return 'sent';
      return 'inbox';
    };
    const folderMessages = React.useMemo(() => {
      if (folder === 'starred') return enrichedMessages.filter((message) => message.isStarred);
      return enrichedMessages.filter((message) => messageFolder(message) === folder);
    }, [enrichedMessages, folder]);
    const filtered = React.useMemo(() => {
      const q = [query, localSearch].map((part) => String(part || '').trim()).filter(Boolean).join(' ').toLowerCase();
      if (!q) return folderMessages;
      return folderMessages.filter((message) => [message.subject, message.from, message.to, message.cc, message.folderName, message.preview, message.connectedProject && projectLabel(message.connectedProject), ...(message.attachments || []).map((item) => item.fileName)].join(' ').toLowerCase().includes(q));
    }, [folderMessages, localSearch, query]);
    const selectedSummary = React.useMemo(() => enrichedMessages.find((message) => message.key === selectedKey) || null, [enrichedMessages, selectedKey]);
    const emailMessageIdFor = React.useCallback((message) => {
      if (!message) return '';
      return message.isDraft ? (message.id || '') : (message.sourceEmailId || message.emailId || message.id || '');
    }, []);

    const loadInbox = React.useCallback(async (refresh = false) => {
      if (scoped || !backendConfigured) return;
      try {
        setLoading(true);
        const next = await window.API.listEmailMessages(50, { refresh });
        setMessages(next);
        setThreadMessages(null);
        setSelectedKey((current) => current || (next[0] ? `email:${next[0].id}` : current));
        if (onUnreadRefresh) onUnreadRefresh();
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    }, [backendConfigured, onError, onUnreadRefresh, scoped]);

    const loadScopedDrafts = React.useCallback(async () => {
      if (!scoped) return;
      try {
        setLoading(true);
        const next = await window.API.listEmailMessages(50, { customerId: scopeCustomerId, projectId: scopeId });
        setScopedDrafts(next);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    }, [onError, scopeCustomerId, scopeId, scoped]);

    const loadDrafts = React.useCallback(async () => {
      if (scoped || !backendConfigured) return;
      try {
        setLoading(true);
        const next = await window.API.listEmailMessages(50, { folder: 'drafts' });
        setDraftMessages(next);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    }, [backendConfigured, onError, scoped]);

    React.useEffect(() => {
      if (!scoped) {
        loadInbox();
        loadDrafts();
      }
    }, [loadDrafts, loadInbox, scoped]);
    React.useEffect(() => { if (scoped) loadScopedDrafts(); }, [loadScopedDrafts, scoped]);
    React.useEffect(() => {
      if (!scoped) return;
      if (!sourceMessages.length) {
        setSelectedKey(null);
        setSelected(null);
        return;
      }
      if (!selectedKey || !sourceMessages.some((message) => message.key === selectedKey)) {
        setSelectedKey(sourceMessages[0].key);
      }
    }, [scoped, selectedKey, sourceMessages]);
    React.useEffect(() => {
      if (!filtered.length) {
        setSelectedKey(null);
        setSelected(null);
        return;
      }
      if (!selectedKey || !filtered.some((message) => message.key === selectedKey)) {
        setSelectedKey(filtered[0].key);
      }
    }, [filtered, selectedKey]);
    React.useEffect(() => {
      setThreadMessages(null);
      setThreadOpen(false);
    }, [selectedKey]);
    React.useEffect(() => {
      if (!selectedKey) { setSelected(null); return; }
      if (!selectedSummary) { setSelected(null); return; }
      const emailId = emailMessageIdFor(selectedSummary) || selectedKey.split(':')[1];
      let cancelled = false;
      const shouldMarkRead = !scoped && selectedSummary && selectedSummary.itemType === 'email' && !selectedSummary.isDraft && selectedSummary.folder === 'inbox' && selectedSummary.isRead === false;
      const request = shouldMarkRead ? window.API.markEmailMessageRead(emailId) : window.API.getEmailMessage(emailId);
      request
        .then((message) => {
          if (cancelled) return;
          setSelected(selectedSummary ? { ...selectedSummary, ...message, projectEmailId: selectedSummary.projectEmailId || selectedSummary.id, status: selectedSummary.status || 'unprocessed' } : message);
          if (shouldMarkRead) {
            setMessages((current) => current.map((item) => item.id === emailId ? { ...item, isRead: true } : item));
            if (onUnreadRefresh) onUnreadRefresh();
          }
        })
        .catch((error) => { if (!cancelled) onError(error); });
      return () => { cancelled = true; };
    }, [emailMessageIdFor, onError, onUnreadRefresh, scoped, selectedKey, selectedSummary]);

    const statusOptions = ['unprocessed', 'in progress', 'awaiting reply', 'processed'];
    const followupTypes = ['Action', 'Remark', 'Decision', 'Risk', 'Question'];
    const statusLabel = (status) => statusOptions.includes(status) ? status[0].toUpperCase() + status.slice(1) : 'Unprocessed';
    const selectedHasStatus = selected && !selected.isDraft && (selected.projectEmailId || selected.itemType === 'email');
    const selectedConnectedProject = selected ? (selected.connectedProject || connectedProjectFor(selected)) : null;
    const projectAssignmentEnabled = showProjectDrop && !scopeId;
    const selectedCanChangeProject = projectAssignmentEnabled && selected && !selected.isDraft && selected.itemType === 'email';
    const selectedHasRequirementFlag = selected && !selected.isDraft && selected.projectEmailId && (scoped || selectedConnectedProject);
    const selectedCanHaveFollowups = selected && !selected.isDraft && selected.itemType === 'email';
    const selectedCanShowThread = selected && !scoped && !selected.isDraft && selected.itemType === 'email';
    const showSelectedThread = async () => {
      if (!selectedCanShowThread) return;
      try {
        setLoading(true);
        const emailId = emailMessageIdFor(selected);
        const next = await window.API.listEmailMessageThread(emailId);
        setThreadMessages(next);
        setThreadOpen(true);
        setSelectedKey(`email:${emailId}`);
        onToast(`${next.length} message${next.length === 1 ? '' : 's'} in thread`);
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    };
    const showAllMessages = () => {
      setThreadMessages(null);
      setThreadOpen(false);
    };
    const updateSelectedStatus = async (status) => {
      if (!selectedHasStatus) return;
      try {
        let next = null;
        const emailId = emailMessageIdFor(selected);
        const projectStatusProject = selected.projectEmailId ? (scoped ? (scopeProject || projectById.get(scopeId)) : selectedConnectedProject) : null;
        if (projectStatusProject && selected.projectEmailId) {
          next = await window.API.updateProjectEmailStatus(projectStatusProject.id, selected.projectEmailId, { status, isRequirement: selected.isRequirement });
        } else {
          next = await window.API.updateEmailMessageStatus(emailId, status);
        }
        setEmailStatusOverrides((current) => {
          const nextOverrides = new Map(current);
          nextOverrides.set(`email:${emailId}`, status);
          if (projectStatusProject && selected.projectEmailId) {
            nextOverrides.set(`project:${projectStatusProject.id}:${selected.projectEmailId}`, status);
          }
          return nextOverrides;
        });
        setMessages((current) => current.map((item) => item.id === emailId ? { ...item, status } : item));
        setSelected({ ...selected, ...next, status });
        onToast('Status updated');
        if (scoped) {
          if (onReload) await onReload();
        } else {
          if (projectStatusProject && onReload) await onReload();
          await loadInbox();
        }
      } catch (error) {
        onError(error);
      }
    };
    const updateSelectedRequirement = async (isRequirement) => {
      if (!selectedHasRequirementFlag) return;
      try {
        const projectId = scoped ? scopeId : selectedConnectedProject.id;
        const next = await window.API.updateProjectEmailStatus(projectId, selected.projectEmailId, { status: selected.status || 'unprocessed', isRequirement });
        setSelected({ ...selected, ...next, isRequirement });
        onToast(isRequirement ? 'Marked as scope source' : 'Unmarked as scope source');
        scoped && onReload ? await onReload() : await loadInbox();
      } catch (error) {
        onError(error);
      }
    };
    const projectEmailPayload = (message) => ({
      emailId: message.sourceEmailId || message.emailId || message.id,
      status: message.status || 'unprocessed',
      workPackageId: message.workPackageId || null,
      isRequirement: !!message.isRequirement
    });
    const updateSelectedProject = async (projectId) => {
      if (!selectedCanChangeProject) return;
      const currentProjectId = selectedConnectedProject ? selectedConnectedProject.id : '';
      const nextProjectId = projectId || '';
      if (nextProjectId === currentProjectId) return;
      const selectedEmailId = emailMessageIdFor(selected);

      try {
        let nextEmail = null;
        const currentLink = projectEmailLinkFor(selected);
        const currentEmailId = selected.projectEmailId || (currentLink && currentLink.id);
        if (currentProjectId && currentEmailId) {
          await window.API.deleteProjectEmail(currentProjectId, currentEmailId);
        }
        if (nextProjectId) {
          nextEmail = await window.API.attachProjectEmail(nextProjectId, projectEmailPayload(selected));
        }

        const nextProject = nextProjectId ? projectById.get(nextProjectId) : null;
        setSelected({
          ...selected,
          sourceEmailId: nextEmail ? nextEmail.sourceEmailId : selected.sourceEmailId,
          workPackageId: nextEmail ? nextEmail.workPackageId : selected.workPackageId,
          attachedAt: nextEmail ? nextEmail.attachedAt : selected.attachedAt,
          id: selectedEmailId || selected.id,
          projectEmailId: nextEmail ? nextEmail.id : null,
          projectId: nextProjectId || null,
          connectedProject: nextProject || null,
          status: (nextEmail && nextEmail.status) || selected.status || 'unprocessed',
          isRequirement: nextEmail ? !!nextEmail.isRequirement : selected.isRequirement
        });
        onToast(nextProject ? `Email attached to ${projectLabel(nextProject)}` : 'Email unlinked from project');
        if (onReload) await onReload();
        if (!scoped) await loadInbox();
      } catch (error) {
        onError(error);
      }
    };
    const addFollowup = async () => {
      if (!selectedCanHaveFollowups) return;
      try {
        const next = await window.API.createEmailMessageFollowup(emailMessageIdFor(selected), { type: 'Action', text: 'Follow up' });
        setSelected({ ...selected, ...next });
        onToast('Follow-up added');
      } catch (error) {
        onError(error);
      }
    };
    const updateFollowup = async (followup, patch) => {
      if (!selectedCanHaveFollowups || !followup.id) return;
      try {
        const next = await window.API.updateEmailMessageFollowup(emailMessageIdFor(selected), followup.id, { ...followup, ...patch });
        setSelected({ ...selected, ...next });
      } catch (error) {
        onError(error);
      }
    };
    const removeFollowup = async (followup) => {
      if (!selectedCanHaveFollowups || !followup.id) return;
      try {
        const next = await window.API.deleteEmailMessageFollowup(emailMessageIdFor(selected), followup.id);
        setSelected({ ...selected, ...next });
        onToast('Follow-up removed');
      } catch (error) {
        onError(error);
      }
    };

    const splitAddresses = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
    const fileIcon = (attachment) => {
      const name = (attachment.fileName || '').toLowerCase();
      const type = (attachment.contentType || '').toLowerCase();
      if (type.includes('pdf') || name.endsWith('.pdf')) return 'filePdf';
      if (type.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/.test(name)) return 'fileImage';
      if (/\.(xls|xlsx|csv|ods)$/.test(name)) return 'fileSheet';
      if (/\.(zip|rar|7z|tar|gz)$/.test(name)) return 'fileArchive';
      return 'doc';
    };
    const formatSize = (size) => {
      if (!size) return '';
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
      return `${(size / 1024 / 1024).toFixed(1)} MB`;
    };
    const openDocumentPicker = () => {
      setDocumentProjectFilter((compose && compose.projectId) || scopeId || '');
      setDocumentSearch('');
      setDocumentPickerOpen(true);
    };
    const filteredDocumentRows = React.useMemo(() => {
      const q = documentSearch.trim().toLowerCase();
      return documentRows.filter((row) => {
        if (documentProjectFilter && row.project.id !== documentProjectFilter) return false;
        if (!q) return true;
        const haystack = [
          row.document.fileName,
          row.document.description,
          row.document.contentType,
          row.document.externalProvider,
          row.document.externalPath,
          row.document.externalUrl,
          row.project.name,
          row.project.customerName,
          row.project.kind
        ].join(' ').toLowerCase();
        return haystack.includes(q);
      });
    }, [documentProjectFilter, documentRows, documentSearch]);
    const attachProjectDocumentToCompose = async (row) => {
      if (!compose || !row || !row.canAttach || attachingDocumentKey) return;
      try {
        setAttachingDocumentKey(row.key);
        const file = await window.API.downloadProjectDocumentFile(row.project.id, row.document.id, row.document.fileName, row.document.contentType);
        setCompose((current) => current ? { ...current, attachments: [...(current.attachments || []), file] } : current);
        setDocumentPickerOpen(false);
        onToast(`Attached ${file.name}`);
      } catch (error) {
        onError(error);
      } finally {
        setAttachingDocumentKey('');
      }
    };
    const prefixedSubject = (prefix, subject) => {
      const clean = subject || '';
      return clean.toLowerCase().startsWith(prefix.toLowerCase()) ? clean : `${prefix} ${clean}`.trim();
    };
    const quote = (message) => `\n\nOn ${new Date(message.date).toLocaleString()}, ${message.from} wrote:\n${message.textBody || message.preview || ''}`.trimEnd();
    const draftPayload = (message) => ({
      to: message.draftTo || splitAddresses(message.to),
      cc: message.draftCc || splitAddresses(message.cc),
      bcc: message.draftBcc || splitAddresses(message.bcc),
      subject: message.subject || '',
      body: message.draftBody ?? message.textBody ?? message.htmlBody ?? '',
      isHtml: !!message.draftIsHtml,
      attachments: [],
      replyToEmailId: message.replyToEmailId || null,
      replyToMessageId: message.replyToMessageId || '',
      customerId: message.customerId || scopeCustomerId || null,
      projectId: message.projectId || scopeId || null,
      projectDeliverableId: message.projectDeliverableId || null
    });
    const startCompose = (mode, message) => {
      const scopeFields = {
        customerId: (message && message.customerId) || scopeCustomerId || null,
        projectId: (message && message.projectId) || scopeId || null,
        projectDeliverableId: (message && message.projectDeliverableId) || null
      };
      if (mode === 'new' || !message) {
        setCompose({ mode: 'new', draftId: null, to: '', cc: '', bcc: '', subject: '', body: '', isHtml: false, attachments: [], replyToEmailId: null, replyToMessageId: '', ...scopeFields });
      } else if (mode === 'draft') {
        const payload = draftPayload(message);
        setCompose({
          mode: 'draft',
          draftId: message.id,
          to: payload.to.join(', '),
          cc: payload.cc.join(', '),
          bcc: payload.bcc.join(', '),
          subject: payload.subject,
          body: payload.body,
          isHtml: payload.isHtml,
          attachments: [],
          replyToEmailId: payload.replyToEmailId,
          replyToMessageId: payload.replyToMessageId,
          customerId: payload.customerId,
          projectId: payload.projectId,
          projectDeliverableId: payload.projectDeliverableId
        });
      } else if (mode === 'reply') {
        setCompose({ mode, draftId: null, to: message.from, cc: '', bcc: '', subject: prefixedSubject('Re:', message.subject), body: quote(message), isHtml: false, attachments: [], replyToEmailId: message.id || null, replyToMessageId: message.messageId || '', ...scopeFields });
      } else if (mode === 'replyAll') {
        setCompose({ mode, draftId: null, to: message.from, cc: [message.to, message.cc].filter(Boolean).join(', '), bcc: '', subject: prefixedSubject('Re:', message.subject), body: quote(message), isHtml: false, attachments: [], replyToEmailId: message.id || null, replyToMessageId: message.messageId || '', ...scopeFields });
      } else if (mode === 'forward') {
        setCompose({ mode, draftId: null, to: '', cc: '', bcc: '', subject: prefixedSubject('Fwd:', message.subject), body: `\n\n---------- Forwarded message ---------\nFrom: ${message.from}\nDate: ${new Date(message.date).toLocaleString()}\nSubject: ${message.subject}\nTo: ${message.to}\n\n${message.textBody || message.preview || ''}`, isHtml: false, attachments: [], replyToEmailId: null, replyToMessageId: '', ...scopeFields });
      }
    };
    const composePayload = () => ({
      ...compose,
      to: splitAddresses(compose.to),
      cc: splitAddresses(compose.cc),
      bcc: splitAddresses(compose.bcc),
      customerId: compose.customerId || scopeCustomerId || null,
      projectId: compose.projectId || scopeId || null,
      projectDeliverableId: compose.projectDeliverableId || null
    });
    const saveDraft = async () => {
      if (sending) return;
      try {
        setSending(true);
        const draft = await window.API.saveEmailDraft(compose.draftId, composePayload());
        setCompose(null);
        setSelectedKey(`email:${draft.id}`);
        if (!scoped) setFolder('drafts');
        onToast('Draft saved');
        scoped ? await loadScopedDrafts() : await loadDrafts();
      } catch (error) {
        onError(error);
      } finally {
        setSending(false);
      }
    };
    const finishSentEmail = async (result, payload) => {
      if (scopeId && result.sentSaved !== false && result.sentUid) {
        await window.API.attachProjectEmail(scopeId, {
          folder: 'sent',
          folderName: result.sentFolder || 'Sent',
          uid: result.sentUid,
          messageId: result.messageId || '',
          from: account,
          to: payload.to.join(', '),
          cc: payload.cc.join(', '),
          subject: payload.subject || '',
          preview: payload.body || '',
          date: new Date().toISOString()
        });
      }
      setCompose(null);
      setSelected(null);
      setSelectedKey(null);
      const message = result.sentSaved === false ? (result.warning || 'Email sent, but no Sent copy was saved') : 'Email sent and saved';
      setSentConfirmation(message);
      onToast(message);
      if (scoped) {
        await loadScopedDrafts();
        if (onReload) await onReload();
      } else {
        await Promise.all([loadInbox(), loadDrafts()]);
      }
    };
    const send = async () => {
      if (sending) return;
      try {
        setSending(true);
        const payload = composePayload();
        const result = compose.draftId
          ? await window.API.saveEmailDraft(compose.draftId, payload).then((draft) => window.API.sendEmailDraft(draft.uid, payload))
          : await window.API.sendEmail(payload);
        await finishSentEmail(result, payload);
      } catch (error) {
        onError(error);
      } finally {
        setSending(false);
      }
    };
    const sendSelectedDraft = async () => {
      if (sending || !selected || !selected.isDraft) return;
      try {
        setSending(true);
        const payload = draftPayload(selected);
        const result = await window.API.sendEmailDraft(selected.id, payload);
        await finishSentEmail(result, payload);
      } catch (error) {
        onError(error);
      } finally {
        setSending(false);
      }
    };
    const removeSelected = async () => {
      const actionLabel = selected && selected.isDraft ? 'Delete' : scoped ? 'Unlink from project' : 'Delete';
      if (!selected || !window.confirm(`${actionLabel} "${selected.subject || '(No subject)'}"?`)) return;
      try {
        if (selected.isDraft) {
          await window.API.deleteEmailDraft(selected.id);
        } else if (scoped && selectedSummary && selectedSummary.id) {
          await window.API.deleteProjectEmail(scopeId, selectedSummary.id);
        } else {
          await window.API.deleteEmailMessage(emailMessageIdFor(selected));
        }
        setSelected(null);
        setSelectedKey(null);
        onToast(selected.isDraft ? 'Draft deleted' : scoped ? 'Email unlinked from project' : 'Email deleted');
        if (selected.isDraft) {
          scoped ? await loadScopedDrafts() : await loadDrafts();
          if (scoped && onReload) await onReload();
        } else {
          scoped && onReload ? await onReload() : await loadInbox();
        }
      } catch (error) {
        onError(error);
      }
    };
    const keyFor = (message) => message ? (message.key || `email:${message.sourceEmailId || message.id}`) : '';
    const toggleStar = (event, message) => {
      if (event) event.stopPropagation();
      const key = keyFor(message);
      if (!key) return;
      setStarredKeys((current) => {
        const next = new Set(current);
        next.has(key) ? next.delete(key) : next.add(key);
        return next;
      });
    };
    const archiveSelected = () => {
      if (!selected) return;
      const key = keyFor(selected);
      setArchivedKeys((current) => {
        const next = new Set(current);
        next.add(key);
        return next;
      });
      setSelected(null);
      setSelectedKey(null);
      setFolder('archive');
      onToast('Email archived');
    };
    const selectFolder = (folderId) => {
      setFolder(folderId);
      if (folderId === 'drafts') {
        loadDrafts();
      }
    };
    const displayAddress = (value) => {
      const text = String(value || '').split(',')[0].trim();
      return text || '';
    };
    const displayName = (value, fallback = 'Unknown sender') => {
      const text = displayAddress(value);
      if (!text) return fallback;
      const match = text.match(/^"?([^"<]+)"?\s*</);
      const name = match ? match[1].trim() : text.split('@')[0].replace(/[._-]+/g, ' ');
      return name || fallback;
    };
    const displayEmail = (value) => {
      const text = displayAddress(value);
      const match = text.match(/<([^>]+)>/);
      return (match ? match[1] : text).trim();
    };
    const messageParty = (message) => message && (message.isDraft || messageFolder(message) === 'sent')
      ? displayName(message.to, 'Unaddressed draft')
      : displayName(message.from, 'Unknown sender');
    const formatShortDate = (value) => {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      const today = new Date();
      if (date.toDateString() === today.toDateString()) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    };
    const formatFullDate = (value) => {
      if (!value) return '';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
    };
    const bodyText = (message) => String((message && (message.draftBody ?? message.textBody)) || (message && message.preview) || 'No readable message body.');
    const attachmentUrl = (message, index) => message && message.isDraft
      ? window.API.getEmailDraftAttachmentUrl(emailMessageIdFor(message), index)
      : window.API.getEmailAttachmentUrl(emailMessageIdFor(message), index);
    const folders = [
      { id: 'inbox', label: 'Inbox', icon: 'inbox', count: enrichedMessages.filter((message) => messageFolder(message) === 'inbox' && message.isRead === false).length },
      { id: 'starred', label: 'Starred', icon: 'star', count: enrichedMessages.filter((message) => message.isStarred).length },
      { id: 'drafts', label: 'Drafts', icon: 'note', count: enrichedMessages.filter((message) => messageFolder(message) === 'drafts').length },
      { id: 'sent', label: 'Sent', icon: 'send', count: 0 },
      { id: 'archive', label: 'Archive', icon: 'archive', count: 0 }
    ];
    const activeFolder = folders.find((item) => item.id === folder) || folders[0];
    const selectedStarred = selected ? (!!selected.isStarred || starredKeys.has(keyFor(selected))) : false;
    const startEmailDrag = (event, message) => {
      if (!projectAssignmentEnabled) return;
      const payload = projectEmailPayload(message);
      setDraggedMessage(payload);
      setDropTargetProjectId(null);
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('application/json', JSON.stringify(payload));
      event.dataTransfer.setData('text/plain', message.subject || '(No subject)');
    };
    const droppedEmail = (event) => {
      const json = event.dataTransfer.getData('application/json');
      if (json) {
        try { return JSON.parse(json); } catch (_) { }
      }

      return draggedMessage;
    };
    const attachToProject = async (event, project) => {
      event.preventDefault();
      if (!projectAssignmentEnabled) return;
      const message = droppedEmail(event);
      if (!message || !message.id) return;
        try {
          await window.API.attachProjectEmail(project.id, message);
          setDraggedMessage(null);
          setDropTargetProjectId(null);
          onToast(`Email attached to ${project.name}`);
          if (onReload) await onReload();
        } catch (error) {
        onError(error);
      }
    };
    const linkOpenMessageToProject = async (project) => {
      if (!selectedCanChangeProject || !project) return;
      await updateSelectedProject(project.id);
    };
    const refreshMessages = () => {
      if (scoped && onReload) return onReload();
      return folder === 'drafts' ? loadDrafts() : loadInbox(true);
    };

    if (!scoped && !backendConfigured) {
      return (
        <Panel title="Email" sub={account || 'No account configured'} actions={<button className="btn ghost" onClick={() => location.hash = '#settings'}><Icon name="settings" size={14} />Open settings</button>}>
          <Empty>Complete backend email access in Settings first.</Empty>
        </Panel>
      );
    }

    return (
      <div className={`email-focus ${projectAssignmentEnabled && projectsOpen ? 'has-projects' : ''} ${workflowOpen ? 'has-workflow' : ''}`} style={{ height: scoped ? 'calc(100vh - 220px)' : 'calc(100vh - 122px)' }}>
        <nav className="ef-rail" aria-label="Mailbox folders">
          <button className="ef-compose" type="button" title="New message" disabled={!backendConfigured} onClick={() => startCompose('new')}>
            <Icon name="plus" size={18} stroke={2} />
          </button>
          {folders.map((item) => (
            <button key={item.id} type="button" className={`ef-folder ${item.id === folder ? 'active' : ''}`} title={item.label} onClick={() => selectFolder(item.id)}>
              <Icon name={item.icon} size={18} />
              {item.count > 0 && <span>{item.count}</span>}
            </button>
          ))}
        </nav>

        <section className="ef-list" aria-label={activeFolder.label}>
          <header className="ef-list-head">
            <div className="ef-list-title">
              <h2>{scopeCustomer ? 'Customer email' : scoped ? 'Project email' : activeFolder.label}</h2>
              <span>{loading ? '...' : filtered.length}</span>
              <button className="icon-btn" type="button" title="Refresh mail" onClick={refreshMessages}><Icon name="refresh" size={15} /></button>
            </div>
            <label className="ef-search">
              <Icon name="search" size={14} />
              <input value={localSearch} onChange={(event) => setLocalSearch(event.target.value)} placeholder="Search mail" />
            </label>
          </header>
          <div className="ef-rows">
            {!filtered.length && <Empty>No messages here.</Empty>}
            {filtered.map((message) => {
              const unread = messageFolder(message) === 'inbox' && message.isRead === false;
              const party = messageParty(message);
              const linkedProject = message.connectedProject || connectedProjectFor(message);
              return (
                <button key={message.key} className={`ef-row ${selectedKey === message.key ? 'selected' : ''} ${unread ? 'unread' : ''}`} type="button" draggable={projectAssignmentEnabled} onDragStart={(event) => startEmailDrag(event, message)} onDragEnd={() => { setDraggedMessage(null); setDropTargetProjectId(null); }} onClick={() => setSelectedKey(message.key)}>
                  <span className="ef-avatar" style={{ background: avatarColor(party) }}>
                    {initials(party)}
                    {unread && <i />}
                  </span>
                  <span className="ef-row-main">
                    <span className="ef-row-top">
                      <strong>{messageFolder(message) === 'sent' || message.isDraft ? `To: ${party}` : party}</strong>
                      <span className="mono">{formatShortDate(message.date)}</span>
                      <span className={`ef-star ${message.isStarred ? 'on' : ''}`} title="Star" onClick={(event) => toggleStar(event, message)}><Icon name="star" size={14} /></span>
                    </span>
                    <span className="ef-subject">{message.subject || '(No subject)'}</span>
                    <span className="ef-preview">{message.preview}</span>
                    <span className="ef-meta">
                      {!!message.status && <Pill kind={message.status} sm>{statusLabel(message.status)}</Pill>}
                      {!!message.isRequirement && <span className="ef-req">REQ</span>}
                      {!!linkedProject && <span className="ef-project-chip"><Icon name="projects" size={11} />{projectLabel(linkedProject)}</span>}
                      {!!(message.attachments || []).length && <Icon name="attach" size={13} />}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section className="ef-reader" aria-label="Message reader">
          {!selected && (
            <div className="ef-empty">
              <Icon name="mail" size={40} />
              <span>Select a message to read.</span>
            </div>
          )}
          {selected && (
            <React.Fragment>
              <header className="ef-reader-head">
                <div className="ef-subject-row">
                  <h1>{selected.subject || '(No subject)'}</h1>
                  <button className={`icon-btn ef-star ${selectedStarred ? 'on' : ''}`} type="button" title="Star" onClick={(event) => toggleStar(event, selected)}><Icon name="star" size={16} /></button>
                </div>
                <div className="ef-sender">
                  <span className="ef-avatar large" style={{ background: avatarColor(messageParty(selected)) }}>{initials(messageParty(selected))}</span>
                  <div>
                    <strong>{displayName(selected.from, account || 'Unknown sender')} <span>&lt;{displayEmail(selected.from)}&gt;</span></strong>
                    <p>to {selected.to || 'undisclosed recipients'} · {formatFullDate(selected.date)}</p>
                  </div>
                </div>
                <div className="ef-actions">
                  {selected.isDraft ? (
                    <button className="btn" type="button" onClick={() => startCompose('draft', selected)}><Icon name="edit" size={14} />Edit draft</button>
                  ) : (
                    <React.Fragment>
                      <button className="btn ghost sm" type="button" onClick={() => startCompose('reply', selected)}><Icon name="reply" size={14} />Reply</button>
                      <button className="btn ghost sm" type="button" title="Reply all" onClick={() => startCompose('replyAll', selected)}><Icon name="replyAll" size={14} /></button>
                      <button className="btn ghost sm" type="button" title="Forward" onClick={() => startCompose('forward', selected)}><Icon name="forward" size={14} /></button>
                    </React.Fragment>
                  )}
                  <span className="spacer" />
                  {projectAssignmentEnabled && <button className={`btn ghost sm ${projectsOpen ? 'active' : ''}`} type="button" onClick={() => setProjectsOpen(!projectsOpen)}><Icon name="projects" size={14} />{projectsOpen ? 'Hide projects' : 'Link to project'}</button>}
                  {!scoped && <button className="icon-btn" type="button" title="Archive" disabled={!selected} onClick={archiveSelected}><Icon name="archive" size={15} /></button>}
                  {!scopeId && <button className="icon-btn danger" type="button" title="Delete" disabled={!selected} onClick={removeSelected}><Icon name="trash" size={15} /></button>}
                  {selectedCanHaveFollowups && <button className={`icon-btn ${workflowOpen ? 'active' : ''}`} type="button" title="Toggle workflow panel" onClick={() => setWorkflowOpen(!workflowOpen)}><Icon name="flow" size={15} /></button>}
                </div>
              </header>

              <div className="ef-reader-body">
                <main className="ef-message-scroll">
                  {sentConfirmation && <div className="alert ef-sent">{sentConfirmation}</div>}
                  {!!(selected.attachments || []).length && (
                    <div className="ef-attachments">
                      {selected.attachments.map((attachment, index) => (
                        <a key={`${attachment.fileName}-${index}`} href={attachmentUrl(selected, index)} download={attachment.fileName} title={`Download ${attachment.fileName}`}>
                          <Icon name={fileIcon(attachment)} size={14} />
                          <strong>{attachment.fileName}</strong>
                          <span>{formatSize(attachment.size)}</span>
                        </a>
                      ))}
                    </div>
                  )}
                  {selectedCanShowThread && (
                    <button className="ef-thread-toggle" type="button" onClick={threadOpen ? showAllMessages : showSelectedThread}>
                      <Icon name="inbox" size={13} />{threadOpen ? 'Hide conversation' : `Show conversation${threadMessages ? ` · ${threadMessages.length} messages` : ''}`}
                    </button>
                  )}
                  {threadOpen && !!(threadMessages || []).length && (
                    <div className="ef-thread">
                      {(threadMessages || []).filter((message) => keyFor(message) !== keyFor(selected)).map((message) => (
                        <article key={keyFor(message)} className="ef-thread-card">
                          <div>
                            <span className="ef-avatar mini" style={{ background: avatarColor(messageParty(message)) }}>{initials(messageParty(message))}</span>
                            <strong>{displayName(message.from)}</strong>
                            <span className="mono">{formatShortDate(message.date)}</span>
                          </div>
                          <p>{bodyText(message)}</p>
                        </article>
                      ))}
                    </div>
                  )}
                  {selected.isDraft && (
                    <div className="ef-draft-actions">
                      <button className="btn" type="button" onClick={() => startCompose('draft', selected)}><Icon name="edit" size={14} />Continue draft</button>
                      <button className="btn ghost" type="button" disabled={sending} onClick={sendSelectedDraft}><Icon name={sending ? 'refresh' : 'send'} size={14} />{sending ? 'Sending...' : 'Send draft'}</button>
                    </div>
                  )}
                  <div className="ef-body-text">
                    {bodyText(selected).split(/\n{2,}/).map((paragraph, index) => <p key={index}>{paragraph}</p>)}
                  </div>
                </main>

                {workflowOpen && selectedCanHaveFollowups && (
                  <aside className="ef-workflow">
                    <h3>Workflow</h3>
                    {selectedHasStatus && (
                      <label className="field">
                        <span>Status</span>
                        <select className="inp" value={selected.status || 'unprocessed'} onChange={(event) => updateSelectedStatus(event.target.value)}>
                          {statusOptions.map((status) => <option key={status} value={status}>{statusLabel(status)}</option>)}
                        </select>
                      </label>
                    )}
                    {projectAssignmentEnabled && (
                      <label className="field">
                        <span>Linked project</span>
                        <select className="inp" value={selectedConnectedProject ? selectedConnectedProject.id : ''} onChange={(event) => updateSelectedProject(event.target.value)}>
                          <option value="">No project</option>
                          {projects.map((project) => <option key={project.id} value={project.id}>{projectLabel(project)}</option>)}
                        </select>
                      </label>
                    )}
                    {!!selectedConnectedProject && <div className="ef-linked">Linked · {projectLabel(selectedConnectedProject)}</div>}
                    {selectedHasRequirementFlag && <label className="checkrow ef-check"><input type="checkbox" checked={!!selected.isRequirement} onChange={(event) => updateSelectedRequirement(event.target.checked)} />Scope source</label>}
                    <div className="ef-follow-head">
                      <h4>Follow-ups</h4>
                      <button type="button" className="btn ghost sm" onClick={addFollowup}><Icon name="plus" size={13} />Add</button>
                    </div>
                    <div className="ef-follow-list">
                      {!(selected.followups || []).length && <div className="mt-att-empty">No follow-ups yet.</div>}
                      {(selected.followups || []).map((followup) => (
                        <div key={followup.id} className={`ef-follow ${String(followup.type || 'Action').toLowerCase()}`}>
                          <div>
                            <select className="inp" value={followup.type || 'Action'} onChange={(event) => updateFollowup(followup, { type: event.target.value })}>
                              {followupTypes.map((type) => <option key={type} value={type}>{type}</option>)}
                            </select>
                            <button type="button" className="icon-btn" title="Delete follow-up" onClick={() => removeFollowup(followup)}><Icon name="trash" size={14} /></button>
                          </div>
                          <textarea className="inp" rows="3" defaultValue={followup.text || ''} onBlur={(event) => updateFollowup(followup, { text: event.target.value })} placeholder="Outcome, action, remark..." />
                        </div>
                      ))}
                    </div>
                  </aside>
                )}
              </div>
            </React.Fragment>
          )}
        </section>

        {projectAssignmentEnabled && projectsOpen && (
          <aside className="ef-projects">
            <header>
              <div>
                <h2>Projects</h2>
                <p>Drag an email here to attach · click a project to link the open message</p>
              </div>
              <button className="icon-btn" type="button" title="Close projects" onClick={() => setProjectsOpen(false)}><Icon name="chevR" size={15} /></button>
            </header>
            <div className="ef-project-list">
              {!projects.length && <Empty>No projects found.</Empty>}
              {projects.map((project) => {
                const isDropTarget = draggedMessage && dropTargetProjectId === project.id;
                const isLinked = selectedConnectedProject && selectedConnectedProject.id === project.id;
                return (
                  <button key={project.id} type="button" className={`ef-project ${isDropTarget ? 'drop' : ''} ${isLinked ? 'linked' : ''}`} onClick={() => linkOpenMessageToProject(project)} onDragEnter={() => setDropTargetProjectId(project.id)} onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; setDropTargetProjectId(project.id); }} onDragLeave={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setDropTargetProjectId(null); }} onDrop={(event) => attachToProject(event, project)}>
                    <span>
                      <strong>{project.name || 'Untitled project'}</strong>
                      <em>{project.status}</em>
                    </span>
                    <small>{project.customerName || project.kind || 'Project'}</small>
                    <span className="ef-project-meta">
                      <span><Icon name="mail" size={11} />{(project.emails || []).length}</span>
                      <span>{project.kind || 'Project'}</span>
                      {isLinked && <b><Icon name="check" size={11} />Linked</b>}
                    </span>
                  </button>
                );
              })}
            </div>
            <footer><Icon name="paperclip" size={13} />Drag a message here to attach it.</footer>
          </aside>
        )}

        {compose && <div className="ef-compose-backdrop" onClick={() => { setDocumentPickerOpen(false); setCompose(null); }} />}
        {compose && (
          <aside className="ef-compose-modal" role="dialog" aria-modal="true">
            <header>
              <h2>{compose.mode === 'new' ? 'New message' : compose.mode === 'draft' ? 'Edit draft' : compose.mode === 'forward' ? 'Forward email' : compose.mode === 'replyAll' ? 'Reply all' : 'Reply'}</h2>
              <button className="icon-btn" type="button" onClick={() => { setDocumentPickerOpen(false); setCompose(null); }}><Icon name="close" size={17} /></button>
            </header>
            <div className="body">
              <datalist id="contact-email-options">
                {contactEmailOptions.map((contact) => <option key={contact.email} value={contact.email} label={contact.label} />)}
              </datalist>
              <label className="field"><span>To</span><input className="inp" type="email" multiple list="contact-email-options" value={compose.to} onChange={(event) => setCompose({ ...compose, to: event.target.value })} /></label>
              <div className="field-row">
                <label className="field"><span>Cc</span><input className="inp" type="email" multiple list="contact-email-options" value={compose.cc} onChange={(event) => setCompose({ ...compose, cc: event.target.value })} /></label>
                <label className="field"><span>Bcc</span><input className="inp" type="email" multiple list="contact-email-options" value={compose.bcc} onChange={(event) => setCompose({ ...compose, bcc: event.target.value })} /></label>
              </div>
              <label className="field"><span>Subject</span><input className="inp" value={compose.subject} onChange={(event) => setCompose({ ...compose, subject: event.target.value })} /></label>
              {projectAssignmentEnabled && (
                <label className="field"><span>Project</span><select className="inp" value={compose.projectId || ''} onChange={(event) => setCompose({ ...compose, projectId: event.target.value || null })}>
                  <option value="">No project</option>
                  {projects.map((project) => <option key={project.id} value={project.id}>{projectLabel(project)}</option>)}
                </select></label>
              )}
              <label className="field"><span>Message</span><textarea className="inp" rows="11" value={compose.body} onChange={(event) => setCompose({ ...compose, body: event.target.value })} /></label>
              <input id="ef-compose-files" hidden type="file" multiple onChange={(event) => setCompose({ ...compose, attachments: [...(compose.attachments || []), ...Array.from(event.target.files || [])] })} />
              {!!(compose.attachments || []).length && <div className="ef-attachments compose-files">
                {compose.attachments.map((file, index) => <button key={`${file.name}-${index}`} type="button" onClick={() => setCompose({ ...compose, attachments: compose.attachments.filter((_, itemIndex) => itemIndex !== index) })}>
                  <Icon name={fileIcon({ fileName: file.name, contentType: file.type })} size={14} /><strong>{file.name}</strong>
                </button>)}
              </div>}
            </div>
            <footer>
              <label className="icon-btn" title="Attach files" htmlFor="ef-compose-files"><Icon name="attach" size={16} /></label>
              <button className="icon-btn" type="button" title="Attach document" onClick={openDocumentPicker}><Icon name="doc" size={16} /></button>
              <span className="spacer" />
              {sending && <span className="t-sub">Working...</span>}
              <button className="btn ghost" type="button" disabled={sending} onClick={saveDraft}><Icon name="check" size={14} />Save draft</button>
              <button className="btn" type="button" disabled={sending} onClick={send}><Icon name={sending ? 'refresh' : 'send'} size={14} />{sending ? 'Sending...' : 'Send'}</button>
            </footer>
            {documentPickerOpen && <div className="ef-doc-picker-backdrop" onClick={() => setDocumentPickerOpen(false)} />}
            {documentPickerOpen && (
              <div className="ef-doc-picker" role="dialog" aria-modal="true" aria-label="Attach document">
                <header>
                  <h3>Attach document</h3>
                  <button className="icon-btn" type="button" title="Close" onClick={() => setDocumentPickerOpen(false)}><Icon name="close" size={15} /></button>
                </header>
                <div className="ef-doc-picker-controls">
                  <label className="field">
                    <span>Project</span>
                    <select className="inp" value={documentProjectFilter} onChange={(event) => setDocumentProjectFilter(event.target.value)}>
                      <option value="">All projects</option>
                      {projects.map((project) => <option key={project.id} value={project.id}>{projectLabel(project)}</option>)}
                    </select>
                  </label>
                  <label className="field">
                    <span>Search</span>
                    <input className="inp" value={documentSearch} onChange={(event) => setDocumentSearch(event.target.value)} placeholder="File name, description, project" autoFocus />
                  </label>
                </div>
                <div className="ef-doc-picker-list">
                  {!documentRows.length && <Empty>No project documents found.</Empty>}
                  {!!documentRows.length && !filteredDocumentRows.length && <Empty>No documents match this filter.</Empty>}
                  {filteredDocumentRows.map((row) => (
                    <button
                      key={row.key}
                      type="button"
                      className={`ef-doc-picker-row ${row.canAttach ? '' : 'disabled'}`}
                      disabled={!row.canAttach || !!attachingDocumentKey}
                      title={row.canAttach ? `Attach ${row.document.fileName}` : 'External link-only documents cannot be attached from the local document store.'}
                      onClick={() => attachProjectDocumentToCompose(row)}>
                      <Icon name={fileIcon(row.document)} size={16} />
                      <span>
                        <strong>{row.document.fileName || 'Untitled document'}</strong>
                        <small>{projectLabel(row.project)}{row.document.description ? ` · ${row.document.description}` : ''}</small>
                      </span>
                      <em>{attachingDocumentKey === row.key ? 'Attaching...' : row.canAttach ? 'Attach' : 'External link'}</em>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}
      </div>
    );
  }


  function Email({ db, query, onToast, onError, onReload, onUnreadRefresh }) {
    return <EmailExplorer db={db} query={query} onToast={onToast} onError={onError} onReload={onReload} onUnreadRefresh={onUnreadRefresh} />;
  }

  Object.assign(window, { Projects, Customers, Email, EmailExplorer, Settings });
})();
